import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { io as ioClient } from "socket.io-client";
import {
  Table,
  Form,
  Row,
  Col,
  Container,
  Button,
  Modal,
  Pagination,
  Card,
  Badge,
  InputGroup,
  Spinner,
  ButtonGroup,
  Offcanvas,
  Dropdown,
} from "react-bootstrap";

import {
  FaIdCard,
  FaPaperPlane,
  FaRegStar,
  FaCalendarAlt,
  FaPaperclip,
  FaTimes,
  FaFileAlt,
  FaDownload,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileAudio,
  FaCheck,
} from "react-icons/fa";
import { TbLabelImportantFilled } from "react-icons/tb";
import { BsFillSendCheckFill, BsChatSquareText } from "react-icons/bs";
import { BiSolidDashboard } from "react-icons/bi";
import {
  MdArchive,
  MdUnarchive,
  MdEmail,
  MdDelete,
  MdOutlineMarkEmailRead,
  MdOutlineTipsAndUpdates,
  MdContentCopy,
  MdSource,
  MdSearch,
  MdInbox,
} from "react-icons/md";
import { RiFileExcel2Fill } from "react-icons/ri";
import { IoMdRefresh, IoMdEye } from "react-icons/io";
import { FaEnvelope, FaEdit, FaStar, FaFilter, FaWhatsapp } from "react-icons/fa";
import { TbCancel } from "react-icons/tb";
import { OverlayTrigger, Popover } from "react-bootstrap";

import { useTheme } from "../components/ThemeContext";
import NavMenu from "../components/NavMenu";
import EmailEditorModal from "../components/EmailEditorModal";
import NewEnquiryModal from "../components/NewEnquiryModal";
import EmailTemplate from "../components/EmailTemplate";
import WhatsappTemplateEditorModal from "../components/WhatsappTemplateEditorModal";
import WhatsappTemplate from "../components/WhatsappTemplate";
import { formatPhoneForDisplay } from "../utils/formatPhone";

import axios from "axios";
import { toast } from "react-toastify";
import Avatar from "../components/Avatar";
import { accessibleBg } from "../utils/color";
import "./enquiryPage.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const API = process.env.REACT_APP_API || "";

// "28-10-2026 (04:06:23)" -> { date: "28-10-2026", time: "04:06:23" }
const splitDateTime = (formatted) => {
  const [date, rest = ""] = String(formatted).split(" (");
  return { date, time: rest.replace(")", "") };
};
const ENDPOINT_AI_TEMPLATE = `${API}/generateAiTemplate`;
const ENDPOINT_GET_SOURCES = `${API}/getSources`;
const ENDPOINT_GET_LABELS = `${API}/getLabels`;
const ENDPOINT_WHATSAPP_MESSAGES = `${API}/whatsappMessages`;
const ENDPOINT_REPLY_WHATSAPP = `${API}/replyWhatsapp`;

// "Email Sent" (replied over email) and "Responded" (replied over
// WhatsApp) both mean the enquiry has been actioned - only "Pending" (or
// anything else/blank) is still outstanding. Module-scope (not a value
// derived from props/state) so it's a stable reference for hook deps.
const isRepliedStatus = (status) =>
  ["email sent", "responded"].includes(
    String(status ?? "").toLowerCase().trim(),
  );

// Phone display formatting (formatPhoneForDisplay) now lives in
// ../utils/formatPhone, shared with Dashboard.js so the two pages can't
// drift out of sync on how WhatsApp-sourced numbers are shown again.

// Renders a message's attachment inline: images/video/audio play directly,
// documents/other files show as an icon + filename + download link.
// Deliberately a standalone component (not inline JSX in the map callback)
// so it can early-return per media type without deeply nested ternaries.
function WhatsappMessageMedia({ msg }) {
  const { message_type, media_url, media_filename, media_mime_type } = msg;
  if (!media_url) return null;

  const commonStyle = { maxWidth: "100%", borderRadius: 8, marginBottom: 6, display: "block" };

  if (message_type === "image" || message_type === "sticker") {
    return (
      <a href={media_url} target="_blank" rel="noopener noreferrer">
        <img src={media_url} alt={media_filename || "attachment"} style={{ ...commonStyle, maxHeight: 260 }} />
      </a>
    );
  }

  if (message_type === "video") {
    return (
      <video controls src={media_url} style={{ ...commonStyle, maxHeight: 260 }}>
        Your browser does not support video playback.
      </video>
    );
  }

  if (message_type === "audio") {
    return (
      <audio controls src={media_url} style={{ width: "100%", marginBottom: 6 }}>
        Your browser does not support audio playback.
      </audio>
    );
  }

  // document / other: icon + filename + explicit download link. PDFs get an
  // inline <embed> preview since browsers render those natively; anything
  // else (Word/Excel/etc.) just gets the icon — no browser can preview those
  // without downloading, so a preview iframe would just show a download
  // prompt anyway.
  const isPdf = media_mime_type === "application/pdf";
  const FileIcon = isPdf
    ? FaFilePdf
    : media_mime_type?.startsWith("audio/")
      ? FaFileAudio
      : media_mime_type?.includes("word")
        ? FaFileWord
        : media_mime_type?.includes("sheet") || media_mime_type?.includes("excel")
          ? FaFileExcel
          : FaFileAlt;

  return (
    <div className="mb-1">
      {isPdf && (
        <embed
          src={media_url}
          type="application/pdf"
          style={{ width: "100%", height: 220, borderRadius: 8, marginBottom: 6 }}
        />
      )}
      <a
        href={media_url}
        target="_blank"
        rel="noopener noreferrer"
        download={media_filename || undefined}
        className="d-flex align-items-center gap-2 text-decoration-none"
        style={{ color: "inherit" }}
      >
        <FileIcon size={20} />
        <span className="text-truncate" style={{ maxWidth: 160 }}>
          {media_filename || "Attachment"}
        </span>
        <FaDownload size={13} />
      </a>
    </div>
  );
}

const Enquire = () => {
  const { isDark } = useTheme();

  const [showModal, setShowModal] = useState(false);
  const [showNewEnquiry, setShowNewEnquiry] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showAdvanceFilterModal, setShowAdvanceFilterModal] = useState(false);
  const [advanceFilterTab, setAdvanceFilterTab] = useState("tag");
  const [showCountSidebar, setShowCountSidebar] = useState(false);
  const [countSidebarTab, setCountSidebarTab] = useState("tag");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  // WhatsApp Template Management — parallels showModal/showTemplateModal
  // above (Create Email Template / View Email Template).
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [showWhatsappTemplateModal, setShowWhatsappTemplateModal] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [replyLogs, setReplyLogs] = useState([]);
  const [loadingReplyLogs, setLoadingReplyLogs] = useState(false);

  // WhatsApp reply-from-dashboard (Source = WhatsApp rows, see Details Modal)
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(false);
  const [whatsappError, setWhatsappError] = useState("");
  const [whatsappReplyText, setWhatsappReplyText] = useState("");
  const [sendingWhatsappReply, setSendingWhatsappReply] = useState(false);
  // Pending attachment for the WhatsApp composer: { file, previewUrl } or null.
  // previewUrl is an object URL for images only, used for the thumbnail preview.
  const [whatsappAttachment, setWhatsappAttachment] = useState(null);
  const [whatsappUploadProgress, setWhatsappUploadProgress] = useState(null); // 0-100 while sending, else null
  const [whatsappSendFailed, setWhatsappSendFailed] = useState(false); // true after a failed send, enables Retry
  const whatsappFileInputRef = useRef(null);
  // Real-time WhatsApp sync (mirrors the standalone TMIS WhatsApp Dashboard's
  // Socket.IO usage — see services/whatsappRelay.js on the backend).
  const whatsappSocketRef = useRef(null);
  const selectedEnquiryIdRef = useRef(null); // kept in sync so the socket listener (registered once) always knows the currently-open enquiry
  const whatsappSeenMessageIdsRef = useRef(new Set()); // dedupes a message that both the initial fetch AND a live socket event could deliver
  // Scrollable message-list container inside the Enquiry Details modal's
  // "WhatsApp Conversation" panel — used to auto-scroll to the latest
  // message (see the effect below), instead of leaving staff to scroll
  // down manually every time the modal opens or a new message arrives.
  const whatsappScrollContainerRef = useRef(null);

  const MAX_WHATSAPP_ATTACHMENT_BYTES = 25 * 1024 * 1024; // keep in sync with WHATSAPP_ATTACHMENT_MAX_BYTES on the server
  const ALLOWED_WHATSAPP_ATTACHMENT_TYPES = [
    "image/", "video/", "audio/", "application/pdf",
    "application/msword", "application/vnd.openxmlformats-officedocument",
    "application/vnd.ms-excel", "application/vnd.ms-powerpoint",
    "text/plain", "text/csv",
  ];

  const [showStarOnly, setShowStarOnly] = useState(false);
  const [showSentOnly, setShowSentOnly] = useState(false);
  const [showNoResponseOnly, setShowNoResponseOnly] = useState(false);
  const [showArchivedOnly, setShowArchivedOnly] = useState(false);

  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templateIdForSending, setTemplateIdForSending] = useState("");

  // WhatsApp Template Management — reply-composer "use template" support,
  // mirrors emailTemplates/templateIdForSending above.
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [whatsappTemplateIdForReply, setWhatsappTemplateIdForReply] = useState("");

  // Bulk "Send WhatsApp" from the toolbar (selected checkboxes, WhatsApp-sourced only).
  const [whatsappTemplateIdForSending, setWhatsappTemplateIdForSending] = useState("");
  const [sendingBulkWhatsapp, setSendingBulkWhatsapp] = useState(false);
  const [bulkWhatsappProgress, setBulkWhatsappProgress] = useState(null);

  // Which channel's list is showing inside the unified toolbar "Template" picker.
  const [templateMenuChannel, setTemplateMenuChannel] = useState("email");
  const [showTemplatePickerModal, setShowTemplatePickerModal] = useState(false);

  // Only one bulk-send template can be active at a time - picking a real
  // template for one channel clears the other. Clearing a channel (id === "")
  // only clears itself.
  const selectEmailTemplateForSending = (id) => {
    setTemplateIdForSending(id);
    if (id) setWhatsappTemplateIdForSending("");
  };
  const selectWhatsappTemplateForSending = (id) => {
    setWhatsappTemplateIdForSending(id);
    if (id) setTemplateIdForSending("");
  };
  // Files pulled back down from a selected WhatsApp template's own
  // attachments (via /api/whatsappTemplateAttachment/:filename), staged
  // alongside whatsappAttachment so they get sent along with the reply.
  // Separate from whatsappAttachment (a single manually-picked file) since
  // a template can carry more than one.
  const [whatsappTemplateAttachments, setWhatsappTemplateAttachments] = useState([]);
  const [loadingWhatsappTemplateAttachments, setLoadingWhatsappTemplateAttachments] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [searchTerm, setSearchTerm] = useState("");

  const [aiReply, setAiReply] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  const [sendingAI, setSendingAI] = useState(false);

  const [showEmailMenuId, setShowEmailMenuId] = useState(null);

  // Compose modal
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [composeUseTemplateId, setComposeUseTemplateId] = useState("");
  const [composeCourse, setComposeCourse] = useState("");
  const [composeTemplateName, setComposeTemplateName] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeFiles, setComposeFiles] = useState([]);
  const [savingAndSending, setSavingAndSending] = useState(false);
  const [savingOnly, setSavingOnly] = useState(false);

  const [aiTplSuggest, setAiTplSuggest] = useState("");
  const [loadingAiTpl, setLoadingAiTpl] = useState(false);
  const [aiTplError, setAiTplError] = useState("");

  const [sortRules, setSortRules] = useState([
    { key: "submission_date", dir: "desc" },
  ]);

  const [sourceList, setSourceList] = useState([]);
  const [labelList, setLabelList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);

  const fileInputRef = React.useRef(null);

  const norm = (v) =>
    String(v ?? "")
      .toLowerCase()
      .trim();

  // Enquiry sources backed by a real, live WhatsApp conversation — mirrors
  // isWhatsappChannelSource in the Enquiry System backend
  // (controllers/ApiService/enquireApi.js). 'Facebook' and 'Instagram'
  // enquiries are WhatsApp conversations that started via a Facebook or
  // Instagram Click-to-WhatsApp ad respectively — they carry a real
  // conversation_id and go through the exact same thread-fetch/reply
  // mechanism as a direct 'WhatsApp' enquiry, so every place here that
  // gates on "is this a WhatsApp conversation" must accept all three, not
  // just the literal string 'whatsapp'. Kept as a single source of truth
  // so a future fourth WhatsApp-entry-point value only needs to be added
  // here.
  const WHATSAPP_CHANNEL_SOURCES = ["whatsapp", "facebook", "instagram"];
  const isWhatsappChannelSource = (source) =>
    WHATSAPP_CHANNEL_SOURCES.includes(norm(source));

  const toggleArrayValue = (value, setter) => {
    const cleanValue = String(value ?? "").trim();
    if (!cleanValue) return;

    setter((prev) =>
      prev.includes(cleanValue)
        ? prev.filter((x) => x !== cleanValue)
        : [...prev, cleanValue],
    );
  };

  const clearAdvanceFilters = () => {
    setSelectedTags([]);
    setSelectedSources([]);
    setCurrentPage(1);
    setSelectedIds([]);
  };

  const formatDate = (dateTimeStr) => {
    if (!dateTimeStr) return "-";
    const date = new Date(dateTimeStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "-";
    const date = new Date(dateTimeStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} (${hours}:${minutes}:${seconds})`;
  };

  const basicSanitize = (html) => {
    if (!html) return "";
    let out = String(html);
    out = out.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "");
    out = out.replace(/\son\w+="[^"]*"/gi, "");
    out = out.replace(/\son\w+='[^']*'/gi, "");
    out = out.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"');
    out = out.replace(/&nbsp;/g, " ");
    return out;
  };

  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const textToHtml = (text) => {
    const t = String(text ?? "");
    if (!t.trim()) return "";
    if (/<[a-z][\s\S]*>/i.test(t)) return t;
    return `<p>${escapeHtml(t).replace(/\n/g, "<br/>")}</p>`;
  };

  const sortIndicatorStyle = {
    fontSize: "12px",
    color: isDark ? "#cbd5e1" : "#475569",
    fontWeight: 500,
  };

  const selectedRange = useMemo(() => {
    return fromDate && toDate
      ? `Showing results from ${formatDate(fromDate)} to ${formatDate(toDate)}`
      : "";
  }, [fromDate, toDate]);

  // kept in sync with getStatusColor in components/Dashboard.js
  const getStatusColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "walk-in":
        return "#3b82f6";
      case "email sent":
        return "#22c55e";
      case "responded":
        return "#0ea5e9";
      case "webenquiry":
        return "#f59e0b";
      case "ssg":
        return "#ef4444";
      case "phone call":
        return "#a855f7";
      case "pending":
        return "#FC6C62";
      case "enquiry":
        return "rgb(251, 107, 244)";
      default:
        return "#64748b";
    }
  };

  // Enquiry lifecycle status shown as a badge in the list: Pending (no reply
  // yet) / Email Sent (replied over email) / Responded (replied over
  // WhatsApp). Everything else falls back to "Pending".
  const getStatusLabel = (status) => {
    const s = norm(status);
    if (s === "email sent") return "Email Sent";
    if (s === "responded") return "Responded";
    return "Pending";
  };

  // ✅ helpers for TAG pill
  const normalizeHex = (c) => {
    const s = String(c || "").trim();
    if (!s) return "";
    return s.startsWith("#") ? s : `#${s}`;
  };

  const getTextColor = (bg) => {
    // returns black/white text, whichever gives the higher WCAG contrast ratio
    // against the given background (guarantees >=4.5:1 for any color)
    const hex = normalizeHex(bg).replace("#", "");
    if (hex.length !== 6) return "#ffffff";
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return "#ffffff";

    const toLinear = (c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const luminance =
      0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

    const contrastWithWhite = 1.05 / (luminance + 0.05);
    const contrastWithBlack = (luminance + 0.05) / 0.05;

    return contrastWithBlack >= contrastWithWhite ? "#111827" : "#ffffff";
  };

  const getTagColor = (row) => {
    // row.color should come from DB (label.color)
    // accessibleBg nudges only mid-tones that would fall under 6:1 with text
    const c = normalizeHex(row?.color);
    if (c) return accessibleBg(c);
    return isDark ? "rgba(255,255,255,0.14)" : "#e9ecef"; // fallback when null
  };

  //Sorting helpers
  const toggleSort = (key) => {
    setCurrentPage(1);

    setSortRules((prev) => {
      const existing = prev.find((x) => x.key === key);
      const others = prev.filter((x) => x.key !== key);

      if (existing) {
        return [
          {
            key,
            dir: existing.dir === "asc" ? "desc" : "asc",
          },
          ...others,
        ];
      }

      return [
        {
          key,
          dir: "asc",
        },
        ...others,
      ];
    });
  };

  const clearSort = () => {
    setSortRules([{ key: "submission_date", dir: "desc" }]);
  };

  const sortIndicator = (key) => {
    const index = sortRules.findIndex((x) => x.key === key);
    if (index === -1) return "";

    const rule = sortRules[index];
    return `${rule.dir === "asc" ? "↑" : "↓"}${index + 1}`;
  };

  const getSortValue = (row, key) => {
    if (key === "row_no") {
      return Number(row.__rowNo || 0);
    }

    const val = row?.[key];

    if (key === "submission_date") {
      return val ? new Date(val).getTime() : 0;
    }

    if (key === "archived" || key === "starred") {
      return Number(val || 0);
    }

    return String(val ?? "")
      .toLowerCase()
      .trim();
  };

  const compareRows = (a, b) => {
    for (const rule of sortRules) {
      const av = getSortValue(a, rule.key);
      const bv = getSortValue(b, rule.key);

      let result = 0;

      if (typeof av === "number" && typeof bv === "number") {
        result = av - bv;
      } else {
        result = String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }

      if (result !== 0) {
        return rule.dir === "asc" ? result : -result;
      }
    }

    return 0;
  };

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/getEnquires`);
      if (response.data.success) setEnquiries(response.data.data || []);
      else console.error("Failed to fetch enquiries:", response.data.message);
    } catch (error) {
      console.error("Error fetching enquiries:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Clears the "new WhatsApp message" badge as soon as staff open the
  // details modal — updates locally right away (so the badge/top-of-list
  // pin disappears instantly) and tells the server so it stays cleared
  // after the next refresh.
  const handleMarkWhatsappSeen = useCallback((row) => {
    if (!row?.has_new_message) return;

    setEnquiries((prev) =>
      prev.map((x) => (x.id === row.id ? { ...x, has_new_message: 0 } : x)),
    );

    axios
      .post(`${API}/markWhatsappSeen`, { id: row.id })
      .catch((error) => console.error("markWhatsappSeen error:", error));
  }, []);

  const fetchSources = useCallback(async () => {
    try {
      const res = await axios.get(ENDPOINT_GET_SOURCES);

      if (res.data?.success) {
        setSourceList(res.data.data || []);
      }
    } catch (err) {
      console.error("getSources error:", err);
    }
  }, []);

  const fetchLabels = useCallback(async () => {
    try {
      const res = await axios.get(ENDPOINT_GET_LABELS);

      if (res.data?.success) {
        setLabelList(res.data.data || []);
      }
    } catch (err) {
      console.error("getLabels error:", err);
    }
  }, []);

  const fetchEmailTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/getEmailTemplate`);
      if (response.data.success) {
        const list = response.data.data || [];
        setEmailTemplates(list);
        return list;
      } else {
        console.error("Failed to load templates:", response.data.message);
        return [];
      }
    } catch (error) {
      console.error("Error fetching templates:", error.message);
      return [];
    }
  }, []);

  // Mirrors fetchEmailTemplates exactly, pointed at the WhatsApp endpoint.
  const fetchWhatsappTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/getWhatsappTemplate`);
      if (response.data.success) {
        const list = response.data.data || [];
        setWhatsappTemplates(list);
        return list;
      } else {
        console.error("Failed to load WhatsApp templates:", response.data.message);
        return [];
      }
    } catch (error) {
      console.error("Error fetching WhatsApp templates:", error.message);
      return [];
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
    fetchEmailTemplates();
    fetchWhatsappTemplates();
    fetchSources();
    fetchLabels();
  }, [fetchEnquiries, fetchEmailTemplates, fetchWhatsappTemplates, fetchSources, fetchLabels]);

  // Auto-refresh the enquiry list every minute so new/updated enquiries
  // show up without a manual "Refresh" click.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchEnquiries();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchEnquiries]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
    // eslint-disable-next-line
  }, [
    fromDate,
    toDate,
    showStarOnly,
    showSentOnly,
    showNoResponseOnly,
    showArchivedOnly,
    searchTerm,
    selectedTags,
    selectedSources,
  ]);

  const filteredData = useMemo(() => {
    const q = norm(searchTerm);

    return enquiries.filter((item) => {
      const archivedMatch = showArchivedOnly
        ? item.archived === 1
        : item.archived !== 1;

      const itemDate = item.submission_date
        ? new Date(item.submission_date)
        : null;
      const start = fromDate ? new Date(fromDate) : null;
      const end = toDate ? new Date(toDate) : null;

      const dateMatch =
        !fromDate || !toDate
          ? true
          : itemDate && start && end
            ? itemDate >= start && itemDate <= end
            : false;

      const starMatch = showStarOnly ? item.starred === 1 : true;
      const sentMatch = showSentOnly ? isRepliedStatus(item.status) : true;
      const noResponseMatch = showNoResponseOnly
        ? !isRepliedStatus(item.status)
        : true;

      const globalMatch = !q
        ? true
        : [
            item.name,
            item.phone_number,
            item.email,
            item.country,
            item.enquiry,
            item.label_name,
            item.source,
            item.status,
          ]
            .map(norm)
            .some((field) => field.includes(q));

      const tagMatch =
        selectedTags.length === 0
          ? true
          : selectedTags.map(norm).includes(norm(item.label_name));

      const sourceMatch =
        selectedSources.length === 0
          ? true
          : selectedSources.map(norm).includes(norm(item.source));

      return (
        archivedMatch &&
        dateMatch &&
        starMatch &&
        sentMatch &&
        noResponseMatch &&
        globalMatch &&
        tagMatch &&
        sourceMatch
      );
    });
  }, [
    enquiries,
    fromDate,
    toDate,
    showStarOnly,
    showSentOnly,
    showNoResponseOnly,
    showArchivedOnly,
    searchTerm,
    selectedTags,
    selectedSources,
  ]);

  // Sort after filter. Rows with a fresh, unseen WhatsApp message always
  // float to the top regardless of the active sort rule, so a customer
  // following up doesn't get buried in the list; everything else falls
  // back to the normal sort within each group.
  const sortedFilteredData = useMemo(() => {
    const copy = filteredData.map((row, index) => ({
      ...row,
      __rowNo: index + 1,
    }));

    copy.sort((a, b) => {
      const aNew = a.has_new_message ? 1 : 0;
      const bNew = b.has_new_message ? 1 : 0;
      return bNew - aNew || compareRows(a, b);
    });

    return copy;
  }, [filteredData, sortRules]);

  const totalPages = Math.ceil(sortedFilteredData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    return sortedFilteredData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );
  }, [sortedFilteredData, currentPage]);

  const counts = useMemo(() => {
    const reply = enquiries.filter(
      (x) => !isRepliedStatus(x.status) && x.archived !== 1,
    ).length;
    const star = enquiries.filter(
      (x) => x.starred === 1 && x.archived !== 1,
    ).length;
    const sent = enquiries.filter(
      (x) => isRepliedStatus(x.status) && x.archived !== 1,
    ).length;
    const archived = enquiries.filter((x) => x.archived === 1).length;
    return { reply, star, sent, archived };
  }, [enquiries]);

  const getSourceColor = (source) => {
    const found = sourceList.find((x) => norm(x.source) === norm(source));

    return accessibleBg(normalizeHex(found?.color) || "#6c757d");
  };

  const handleRefresh = () => {
    setFromDate("");
    setToDate("");
    setSearchTerm("");
    setShowSentOnly(false);
    setShowStarOnly(false);
    setShowNoResponseOnly(false);
    setShowArchivedOnly(false);
    setSelectedIds([]);
    setSelectedTags([]);
    setSelectedSources([]);
    setSortRules([{ key: "submission_date", dir: "desc" }]);
    fetchEnquiries();
  };

  const toggleStar = async (id, currentStarred) => {
    const newStarValue = currentStarred === 1 ? 0 : 1;
    try {
      const response = await axios.post(`${API}/addImportance`, {
        id,
        star: newStarValue,
      });
      if (response.data.success) {
        const updated = enquiries.map((item) =>
          item.id === id ? { ...item, starred: newStarValue } : item,
        );
        setEnquiries(updated);
      }
    } catch (err) {
      console.error("API Error:", err);
    }
  };

  const toggleArchive = async (id, currentArchived) => {
    const newArchiveValue = currentArchived === 1 ? 0 : 1;
    try {
      const response = await axios.post(`${API}/archived`, {
        id,
        archive: newArchiveValue,
      });

      if (response.data.success) {
        const updated = enquiries.map((item) =>
          item.id === id ? { ...item, archived: newArchiveValue } : item,
        );
        setEnquiries(updated);
      } else {
        alert(response.data.message || "Failed to update archive.");
      }
    } catch (err) {
      console.error("Archive API Error:", err);
      alert("Server error. Could not update archive.");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0)
      return alert("Please select at least one enquiry to delete.");
    if (!window.confirm("Are you sure you want to delete selected enquiries?"))
      return;

    try {
      const response = await axios.post(`${API}/deleteMultipleEnquiries`, {
        ids: selectedIds,
      });
      if (response.data.success) {
        alert("Selected enquiries deleted.");
        setSelectedIds([]);
        fetchEnquiries();
      } else alert("Failed to delete selected enquiries.");
    } catch (error) {
      console.error("Delete error:", error.message);
      alert("Server error. Could not delete.");
    }
  };

  // Excel export
  const buildWorksheetData = (rows) =>
    rows.map((item, index) => ({
      No: index + 1,
      Date: formatDate(item.submission_date),
      Name: item.name,
      "Phone Number": item.phone_number,
      Email: item.email,
      Country: item.country,
      Enquiry: item.enquiry,
      Status: item.status,
      Archived: item.archived === 1 ? "Yes" : "No",
    }));

  const downloadExcel = (rows, filenamePrefix) => {
    const worksheetData = buildWorksheetData(rows);
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);

    worksheet["!cols"] = [
      { wch: 6 },
      { wch: 14 },
      { wch: 22 },
      { wch: 16 },
      { wch: 28 },
      { wch: 14 },
      { wch: 50 },
      { wch: 14 },
      { wch: 10 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

    const dateStr = new Date().toISOString().slice(0, 10);
    saveAs(blob, `${filenamePrefix}_${dateStr}.xlsx`);
  };

  const handleExportSelectedToExcel = () => {
    if (selectedIds.length === 0)
      return alert("Please select at least one enquiry to export.");
    const selectedRows = sortedFilteredData.filter((x) =>
      selectedIds.includes(x.id),
    );
    if (selectedRows.length === 0)
      return alert("Selected rows not found in current filter.");
    downloadExcel(selectedRows, "Selected_Enquiries");
  };

  const handleSendEmail = async (id) => {
    if (!templateIdForSending)
      return alert("Please select an email template to send.");

    const created_by = JSON.parse(localStorage.getItem("user"))?.user_name;
    if (!created_by) return alert("User not found. Please log in again.");

    try {
      const response = await axios.post(`${API}/sendEmail`, {
        ids: [id],
        template_id: Number(templateIdForSending),
        created_by,
      });

      if (response.data.success) {
        alert("Email sending started.");
        fetchEnquiries();
      } else alert(response.data.message || "Failed to send email.");
    } catch (error) {
      console.error("Send email error:", error);
      alert("An error occurred while sending email.");
    }
  };

  const handleSendBulkEmail = async () => {
    if (!templateIdForSending)
      return alert("Please select an email template first.");
    if (selectedIds.length === 0)
      return alert("Please select at least one enquiry to send email.");

    const created_by = JSON.parse(localStorage.getItem("user"))?.user_name;
    if (!created_by) return alert("User not found. Please log in again.");

    try {
      const response = await axios.post(`${API}/sendEmail`, {
        ids: selectedIds,
        template_id: Number(templateIdForSending),
        created_by,
      });

      if (response.data.success) {
        alert("Email sending started.");
        fetchEnquiries();
      } else alert(response.data.message || "Failed to send emails.");
    } catch (error) {
      console.error("Send email error:", error);
      alert("An error occurred while sending emails.");
    }
  };

  // Bulk "Send WhatsApp" from the toolbar. Reuses the same /replyWhatsapp
  // endpoint as the single-conversation reply panel (one call per recipient
  // conversation), token-replacing the template text per-enquiry the same
  // way buildComposeFromTemplate does for email. Restricted to selected
  // rows whose source is a WhatsApp-channel source (WhatsApp or Facebook —
  // see isWhatsappChannelSource) - anything else in the selection is
  // skipped, since a non-WhatsApp-channel enquiry has no conversation to
  // reply to.
  const handleSendBulkWhatsapp = async () => {
    if (!whatsappTemplateIdForSending) {
      return alert("Please choose a WhatsApp template first.");
    }
    if (selectedIds.length === 0) {
      return alert("Please select at least one enquiry to send WhatsApp to.");
    }

    const tpl = findWhatsappTemplateById(whatsappTemplateIdForSending);
    if (!tpl) return alert("Selected WhatsApp template not found.");

    const selectedRows = enquiries.filter((e) => selectedIds.includes(e.id));
    const waRows = selectedRows.filter((e) => isWhatsappChannelSource(e.source));

    if (waRows.length === 0) {
      return alert(
        "None of the selected enquiries are from a WhatsApp conversation. Send WhatsApp only sends to WhatsApp/Facebook/Instagram-sourced enquiries.",
      );
    }

    const skipped = selectedRows.length - waRows.length;
    if (skipped > 0) {
      const proceed = window.confirm(
        `${waRows.length} of ${selectedRows.length} selected enquiries are from a WhatsApp conversation and will receive this message. ` +
          `${skipped} other enquiries will be skipped. Continue?`,
      );
      if (!proceed) return;
    }

    let filenames = [];
    try {
      const parsed = JSON.parse(tpl.attachments || "[]");
      filenames = Array.isArray(parsed) ? parsed : [];
    } catch {
      filenames = [];
    }

    setSendingBulkWhatsapp(true);
    setBulkWhatsappProgress({ done: 0, total: waRows.length });

    let attachmentFiles = [];
    if (filenames.length) {
      try {
        const staged = await Promise.all(
          filenames.map(async (filename) => {
            try {
              const res = await axios.get(
                `${API}/whatsappTemplateAttachment/${encodeURIComponent(filename)}`,
                { responseType: "blob" },
              );
              return new File([res.data], filename, {
                type: res.data.type || "application/octet-stream",
              });
            } catch (err) {
              console.error(`Failed to fetch template attachment "${filename}":`, err.message);
              return null;
            }
          }),
        );
        attachmentFiles = staged.filter(Boolean);
      } catch (err) {
        console.error("Error staging bulk WhatsApp attachments:", err);
      }
    }

    const results = { sent: 0, failed: [] };

    for (const row of waRows) {
      const text = replaceTokens(tpl.message || "", row).trim();
      try {
        if (attachmentFiles.length === 0) {
          if (!text) throw new Error("Template has no message text or attachments.");
          const res = await axios.post(ENDPOINT_REPLY_WHATSAPP, {
            enquiry_id: row.id,
            message: text,
          });
          if (!res.data?.success) throw new Error(res.data?.message || "Send failed.");
        } else {
          if (text) {
            const res = await axios.post(ENDPOINT_REPLY_WHATSAPP, {
              enquiry_id: row.id,
              message: text,
            });
            if (!res.data?.success) throw new Error(res.data?.message || "Send failed.");
          }
          for (const file of attachmentFiles) {
            const form = new FormData();
            form.append("enquiry_id", row.id);
            form.append("attachment", file, file.name);
            const res = await axios.post(ENDPOINT_REPLY_WHATSAPP, form);
            if (!res.data?.success) throw new Error(res.data?.message || "Attachment send failed.");
          }
        }
        results.sent += 1;
        // Same as the single-reply path: the server just marked this
        // enquiry status = 'responded' — reflect it in the table now
        // instead of leaving every sent row showing "Pending" until the
        // next auto-refresh.
        setEnquiries((prev) =>
          prev.map((x) =>
            x.id === row.id
              ? { ...x, status: "responded", has_new_message: 0 }
              : x,
          ),
        );
      } catch (err) {
        results.failed.push({
          name: row.name || `#${row.id}`,
          reason: err?.response?.data?.message || err.message,
        });
      }
      setBulkWhatsappProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
    }

    setSendingBulkWhatsapp(false);
    setBulkWhatsappProgress(null);
    setWhatsappTemplateIdForSending("");
    setSelectedIds([]);
    fetchEnquiries();

    if (results.failed.length === 0) {
      alert(`WhatsApp message sent to ${results.sent} enquir${results.sent === 1 ? "y" : "ies"}.`);
    } else {
      alert(
        `Sent to ${results.sent} of ${waRows.length}.\n\nFailed:\n` +
          results.failed.map((f) => `- ${f.name}: ${f.reason}`).join("\n"),
      );
    }
  };

  const handleAISupport = async () => {
    if (!selectedEnquiry) return;

    setLoadingAI(true);
    try {
      const response = await axios.post(`${API}/generateAiReply`, {
        name: selectedEnquiry.name,
        enquiry: selectedEnquiry.enquiry,
      });

      if (response.data.success) setAiReply(response.data.reply);
      else alert("Failed to generate reply");
    } catch (err) {
      console.error("AI error:", err);
      alert("Error contacting AI.");
    } finally {
      setLoadingAI(false);
    }
  };

  const handleReviewEmail = async () => {
    if (!selectedEnquiry) return;

    setLoadingReplyLogs(true);
    setReplyLogs([]);
    setShowReviewModal(true);

    try {
      const res = await axios.get(`${API}/getreplyEmail`, {
        params: { enquire: selectedEnquiry.id },
      });

      if (res.data?.success) setReplyLogs(res.data.data || []);
      else alert(res.data?.message || "Failed to load review emails.");
    } catch (err) {
      console.error("getreplyEmail error:", err);
      alert("Server error while loading email details.");
    } finally {
      setLoadingReplyLogs(false);
    }
  };

  // Loads the mirrored WhatsApp thread for the enquiry currently open in the
  // Details Modal. Only called when source === "WhatsApp"; the backend
  // proxies this to whatsapp.touristguide.sg using the enquiry's stored
  // conversation_id (see saveWhatsappEnquire on the server).
  const fetchWhatsappMessages = useCallback(async (enquiryId) => {
    setLoadingWhatsapp(true);
    setWhatsappError("");
    setWhatsappMessages([]);

    try {
      const res = await axios.get(ENDPOINT_WHATSAPP_MESSAGES, {
        params: { enquire_id: enquiryId },
      });

      if (res.data?.success) {
        const messages = res.data.messages || [];
        whatsappSeenMessageIdsRef.current = new Set(messages.map((m) => m.id).filter(Boolean));
        setWhatsappMessages(messages);
      } else {
        setWhatsappError(
          res.data?.message || "Failed to load WhatsApp conversation.",
        );
      }
    } catch (err) {
      console.error("whatsappMessages error:", err);
      setWhatsappError(
        err?.response?.data?.message ||
          "Server error while loading the WhatsApp conversation.",
      );
    } finally {
      setLoadingWhatsapp(false);
    }
  }, []);

  // Validates and stages a file for the WhatsApp composer (does not upload
  // yet — upload happens on send, same as the existing text flow).
  const handleWhatsappFileSelect = (file) => {
    if (!file) return;
    setWhatsappError("");

    if (file.size > MAX_WHATSAPP_ATTACHMENT_BYTES) {
      setWhatsappError(
        `"${file.name}" is too large (max ${(MAX_WHATSAPP_ATTACHMENT_BYTES / (1024 * 1024)).toFixed(0)}MB).`,
      );
      return;
    }
    if (!ALLOWED_WHATSAPP_ATTACHMENT_TYPES.some((p) => file.type?.startsWith(p))) {
      setWhatsappError(`"${file.name}" is not a supported file type.`);
      return;
    }

    const previewUrl = file.type?.startsWith("image/") ? URL.createObjectURL(file) : null;
    setWhatsappAttachment({ file, previewUrl });
  };

  const handleRemoveWhatsappAttachment = () => {
    if (whatsappAttachment?.previewUrl) URL.revokeObjectURL(whatsappAttachment.previewUrl);
    setWhatsappAttachment(null);
    if (whatsappFileInputRef.current) whatsappFileInputRef.current.value = "";
  };

  const handleWhatsappDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleWhatsappFileSelect(file);
  };

  // Releases any object URLs created for image previews of staged template
  // attachments, then clears the staged list — mirrors
  // handleRemoveWhatsappAttachment's cleanup for the single manual file.
  const clearWhatsappTemplateAttachments = () => {
    setWhatsappTemplateAttachments((prev) => {
      prev.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
      return [];
    });
  };

  const handleRemoveWhatsappTemplateAttachment = (filename) => {
    setWhatsappTemplateAttachments((prev) => {
      const target = prev.find((a) => a.filename === filename);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.filename !== filename);
    });
  };

  // Applying a WhatsApp template to the reply composer — same idea as
  // buildComposeFromTemplate for email (token-substitute the saved text
  // against the currently selected enquiry), just written directly into
  // whatsappReplyText since the WhatsApp reply panel has no separate
  // compose modal to populate. Also pulls the template's own attachments
  // back down as real Files (via /api/whatsappTemplateAttachment) and
  // stages them so they go out with the reply too.
  const handleSelectWhatsappTemplate = async (id) => {
    setWhatsappTemplateIdForReply(id);
    clearWhatsappTemplateAttachments();

    if (!id) {
      return;
    }
    const tpl = findWhatsappTemplateById(id);
    if (!tpl) return;

    setWhatsappReplyText(replaceTokens(tpl.message || "", selectedEnquiry));

    let filenames = [];
    try {
      const parsed = JSON.parse(tpl.attachments || "[]");
      filenames = Array.isArray(parsed) ? parsed : [];
    } catch {
      filenames = [];
    }
    if (!filenames.length) return;

    setLoadingWhatsappTemplateAttachments(true);
    try {
      const staged = await Promise.all(
        filenames.map(async (filename) => {
          try {
            const res = await axios.get(
              `${API}/whatsappTemplateAttachment/${encodeURIComponent(filename)}`,
              { responseType: "blob" }
            );
            const file = new File([res.data], filename, {
              type: res.data.type || "application/octet-stream",
            });
            const previewUrl = file.type.startsWith("image/")
              ? URL.createObjectURL(file)
              : null;
            return { filename, file, previewUrl };
          } catch (err) {
            console.error(`Failed to fetch template attachment "${filename}":`, err.message);
            return null;
          }
        })
      );
      // Only apply if the user hasn't switched templates again while this
      // was in flight.
      setWhatsappTemplateIdForReply((current) => {
        if (String(current) === String(id)) {
          setWhatsappTemplateAttachments(staged.filter(Boolean));
        }
        return current;
      });
    } finally {
      setLoadingWhatsappTemplateAttachments(false);
    }
  };

  // A single call to /api/replyWhatsapp only accepts one attachment (see
  // replyWhatsapp in enquireApi.js, which proxies to the WhatsApp service's
  // own single-attachment /reply endpoint). When more than one file is
  // staged — e.g. a template with several attachments, or a template
  // attachment plus a manually-added one — the text goes out as its own
  // message first, then each file goes out as its own follow-up message,
  // same as how WhatsApp itself threads multiple attachments as separate
  // messages rather than one combined one.
  const sendSingleWhatsappPart = async ({ message, file }) => {
    if (file) {
      const form = new FormData();
      form.append("enquiry_id", selectedEnquiry.id);
      if (message) form.append("message", message);
      form.append("attachment", file, file.name);
      return axios.post(ENDPOINT_REPLY_WHATSAPP, form, {
        onUploadProgress: (evt) => {
          if (evt.total) setWhatsappUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
    }
    return axios.post(ENDPOINT_REPLY_WHATSAPP, {
      enquiry_id: selectedEnquiry.id,
      message,
    });
  };

  const handleSendWhatsappReply = async () => {
    if (!selectedEnquiry) return;

    const text = whatsappReplyText.trim();
    const files = [
      ...(whatsappAttachment ? [whatsappAttachment.file] : []),
      ...whatsappTemplateAttachments.map((a) => a.file),
    ];

    if (!text && files.length === 0) return;

    setSendingWhatsappReply(true);
    setWhatsappError("");
    setWhatsappSendFailed(false);
    setWhatsappUploadProgress(files.length ? 0 : null);

    try {
      if (files.length <= 1) {
        // Single part: text (as a caption, if any) plus at most one file —
        // identical to the original single-attachment flow.
        const res = await sendSingleWhatsappPart({
          message: text || undefined,
          file: files[0] || null,
        });
        if (!res.data?.success) {
          setWhatsappError(res.data?.message || "Failed to send WhatsApp reply.");
          setWhatsappSendFailed(true);
          return;
        }
      } else {
        // Multiple files: send the text alone first (if present), then
        // each file as its own message, in order.
        if (text) {
          const textRes = await sendSingleWhatsappPart({ message: text, file: null });
          if (!textRes.data?.success) {
            setWhatsappError(textRes.data?.message || "Failed to send WhatsApp reply.");
            setWhatsappSendFailed(true);
            return;
          }
        }
        for (let i = 0; i < files.length; i++) {
          const fileRes = await sendSingleWhatsappPart({ message: undefined, file: files[i] });
          if (!fileRes.data?.success) {
            setWhatsappError(
              fileRes.data?.message ||
                `Failed to send attachment "${files[i].name}". Earlier parts of this reply were already sent.`
            );
            setWhatsappSendFailed(true);
            return;
          }
        }
      }

      setWhatsappReplyText("");
      setWhatsappTemplateIdForReply("");
      handleRemoveWhatsappAttachment();
      clearWhatsappTemplateAttachments();
      // No re-fetch needed here anymore: the WhatsApp service mirrors this
      // send and pushes it back via Socket.IO (see whatsapp:message
      // listener below), exactly like the standalone TMIS WhatsApp
      // Dashboard — deliberately no optimistic append, to guarantee the
      // thread only ever shows what's actually been mirrored.

      // The server just marked this enquiry status = 'responded' (see
      // replyWhatsapp) — reflect that in the table right away instead of
      // leaving the row showing "Pending" until the next auto-refresh.
      setEnquiries((prev) =>
        prev.map((x) =>
          x.id === selectedEnquiry.id
            ? { ...x, status: "responded", has_new_message: 0 }
            : x,
        ),
      );
      setSelectedEnquiry((prev) =>
        prev && prev.id === selectedEnquiry.id
          ? { ...prev, status: "responded" }
          : prev,
      );
    } catch (err) {
      console.error("replyWhatsapp error:", err);
      setWhatsappError(
        err?.response?.data?.message ||
          "Server error while sending the WhatsApp reply.",
      );
      setWhatsappSendFailed(true);
    } finally {
      setSendingWhatsappReply(false);
      setWhatsappUploadProgress(null);
    }
  };


  useEffect(() => {
    if (!showDetailsModal || !selectedEnquiry) return;
    if (!isWhatsappChannelSource(selectedEnquiry.source)) return;

    setWhatsappReplyText("");
    setWhatsappTemplateIdForReply("");
    handleRemoveWhatsappAttachment();
    clearWhatsappTemplateAttachments();
    setWhatsappSendFailed(false);
    fetchWhatsappMessages(selectedEnquiry.id);

    selectedEnquiryIdRef.current = selectedEnquiry.id;
    whatsappSocketRef.current?.emit("join", { enquiryId: selectedEnquiry.id });

    // Leave the room when this enquiry's modal closes or a different one
    // opens — otherwise this socket would keep receiving live updates for
    // every WhatsApp enquiry ever opened in the session.
    return () => {
      whatsappSocketRef.current?.emit("leave", { enquiryId: selectedEnquiry.id });
      if (selectedEnquiryIdRef.current === selectedEnquiry.id) {
        selectedEnquiryIdRef.current = null;
      }
    };
    // Only re-run when a different enquiry is opened, or the modal opens —
    // not on every selectedEnquiry object identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetailsModal, selectedEnquiry?.id]);

  // Auto-scroll the WhatsApp thread to the latest message. Fires in two
  // situations, both of which previously left staff scrolling manually:
  //   1. The conversation just finished loading (modal opened via the eye
  //      icon, or the manual refresh button was pressed) — scroll straight
  //      to the bottom instead of showing the top of the history.
  //   2. `whatsappMessages` grew — either a live inbound message arrived
  //      via the whatsapp:message socket event, or staff just sent an
  //      outbound reply and it was mirrored back (see handleSendWhatsappReply's
  //      comment: no optimistic append, so this is the only path new
  //      outbound messages ever appear through too).
  // Runs after render (DOM already updated), so scrollHeight reflects the
  // newly-rendered messages.
  useEffect(() => {
    if (loadingWhatsapp) return;
    const el = whatsappScrollContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [whatsappMessages, loadingWhatsapp]);

  // Real-time WhatsApp sync: one persistent Socket.IO connection to THIS
  // backend (enquiry.tmis.edu.sg), which itself relays events from the
  // WhatsApp mirror service (see services/whatsappRelay.js) — mirrors how
  // the standalone TMIS WhatsApp Dashboard uses Socket.IO, just with an
  // extra hop so that service's send-authorizing token never reaches the
  // browser. Connects once for the lifetime of this component; join/leave
  // (above) scopes which enquiry's events actually reach this client.
  useEffect(() => {
    let storedUser = null;
    try {
      storedUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      storedUser = null;
    }

    /* const socket = ioClient(API || undefined, {
      withCredentials: true,
      auth: { token: storedUser?.token || undefined },
    }); */
    const SOCKET_URL = (API || "").replace(/\/api\/?$/, "") || undefined;
    const socket = ioClient(SOCKET_URL, {
      withCredentials: true,
      auth: { token: storedUser?.token || undefined },
    });
    whatsappSocketRef.current = socket;

    socket.on("connect_error", (err) => {
      console.error("WhatsApp real-time connection error:", err.message);
    });

    socket.on("whatsapp:message", (msg) => {
      // Only apply if this event is for the enquiry currently open — the
      // backend already scopes delivery via Socket.IO rooms (join/leave
      // above), but this is a cheap extra guard against any stale event.
      if (String(msg.enquiryId) !== String(selectedEnquiryIdRef.current)) return;

      // Dedupe: the initial REST fetch and a live socket event could both
      // deliver the same message if one arrives right as the modal opens.
      if (msg.id != null) {
        if (whatsappSeenMessageIdsRef.current.has(msg.id)) return;
        whatsappSeenMessageIdsRef.current.add(msg.id);
      }

      setWhatsappMessages((prev) => [
        ...prev,
        {
          id: msg.id,
          direction: msg.direction,
          message_type: msg.messageType,
          message_content: msg.messageContent,
          media_mime_type: msg.mediaMimeType,
          media_filename: msg.mediaFilename,
          media_url: msg.mediaUrl,
          media_filesize: msg.mediaFilesize,
          caption: msg.caption,
          timestamp: msg.timestamp,
        },
      ]);

      // The backend just flagged this enquiry has_new_message = 1 (see
      // services/whatsappRelay.js) so it pins to the top with a badge in
      // the list — but staff are looking straight at this conversation
      // right now, so immediately clear it back rather than have the badge
      // flash on next refresh for something already being watched live.
      setEnquiries((prev) =>
        prev.map((x) =>
          String(x.id) === String(msg.enquiryId)
            ? { ...x, has_new_message: 0 }
            : x,
        ),
      );
      axios
        .post(`${API}/markWhatsappSeen`, { id: msg.enquiryId })
        .catch((error) => console.error("markWhatsappSeen error:", error));
    });

    // List-wide notice (see ENQUIRIES_LIST_ROOM in services/whatsappRelay.js)
    // — every connected client gets this regardless of which enquiry, if
    // any, they have open, so a new WhatsApp message pins that row to the
    // top of the Enquiries table and shows its badge immediately instead of
    // waiting for the next auto-refresh. Skipped for the enquiry currently
    // open: the "whatsapp:message" handler above already takes care of that
    // one (append to the live thread + clear the flag straight back).
    socket.on("whatsapp:new-message-flag", ({ enquiryId }) => {
      if (String(enquiryId) === String(selectedEnquiryIdRef.current)) return;

      setEnquiries((prev) =>
        prev.map((x) =>
          String(x.id) === String(enquiryId)
            ? { ...x, has_new_message: 1 }
            : x,
        ),
      );
    });

    // Surfaces email send failures that used to only ever show up as a
    // console.error nobody was watching (see notifyEmailSendFailed on the
    // server) — every connected staff client gets this via
    // ENQUIRIES_LIST_ROOM, whoever actually triggered the send included.
    socket.on("email:send-failed", ({ recipientEmail, reason }) => {
      toast.error(
        `Email to ${recipientEmail || "recipient"} failed: ${reason || "Unknown error."}`,
        { autoClose: 10000 },
      );
    });

    return () => {
      socket.disconnect();
      whatsappSocketRef.current = null;
    };
    // Deliberately mount-once: this connection lives for the component's
    // lifetime, independent of which enquiry/modal is currently open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEmailMenu = (rowId) => setShowEmailMenuId(rowId);
  const closeEmailMenu = () => setShowEmailMenuId(null);

  const findTemplateById = useCallback(
    (id) => emailTemplates.find((t) => String(t.template_id) === String(id)),
    [emailTemplates],
  );

  // Mirrors findTemplateById exactly, over whatsappTemplates instead.
  const findWhatsappTemplateById = useCallback(
    (id) => whatsappTemplates.find((t) => String(t.template_id) === String(id)),
    [whatsappTemplates],
  );

  const replaceTokens = (text, enquiry) => {
    const map = {
      "{{name}}": enquiry?.name || "",
      "{{email}}": enquiry?.email || "",
      "{{phone}}": enquiry?.phone_number || "",
      "{{country}}": enquiry?.country || "",
      "{{enquiry}}": enquiry?.enquiry || "",
    };
    let out = String(text ?? "");
    Object.keys(map).forEach((k) => {
      out = out.split(k).join(map[k]);
    });
    return out;
  };

  const buildComposeFromTemplate = (tpl, enquiry) => {
    const rawSubject = tpl?.email_subject || "";
    const rawBody = tpl?.description || "";
    const subject = replaceTokens(rawSubject, enquiry);
    const body = replaceTokens(rawBody, enquiry);
    const course = tpl?.course || "";
    return { subject, body, course };
  };

  const resetCompose = () => {
    setComposeUseTemplateId("");
    setComposeCourse("");
    setComposeTemplateName("");
    setComposeSubject("");
    setComposeBody("");
    setComposeFiles([]);

    setAiTplSuggest("");
    setAiTplError("");
    setLoadingAiTpl(false);
  };

  const handleWriteEmail = (row) => {
    setSelectedEnquiry(row);

    const initialTemplateId = templateIdForSending
      ? String(templateIdForSending)
      : "";
    setComposeUseTemplateId(initialTemplateId);

    setAiTplSuggest("");
    setAiTplError("");
    setLoadingAiTpl(false);

    if (initialTemplateId) {
      const tpl = findTemplateById(initialTemplateId);
      const built = buildComposeFromTemplate(tpl, row);
      setComposeSubject(built.subject || "");
      setComposeBody(textToHtml(built.body || ""));
      setComposeCourse(built.course || "");
      setComposeTemplateName(`(Copy) ${tpl?.template_name || "New Template"}`);
    } else {
      setComposeSubject("");
      const defaultText = row?.enquiry
        ? `Dear ${row?.name || ""},\n\nThank you for your enquiry:\n"${row.enquiry}"\n\nPlease write email to send`
        : `Dear ${row?.name || ""},\n\nPlease write email to send`;

      setComposeBody(textToHtml(defaultText));
      setComposeCourse("");
      setComposeTemplateName("");
    }

    setShowWriteModal(true);
    closeEmailMenu();
  };

  useEffect(() => {
    if (!showWriteModal) return;
    if (!selectedEnquiry) return;

    if (!composeUseTemplateId) return;
    const tpl = findTemplateById(composeUseTemplateId);
    if (!tpl) return;

    const built = buildComposeFromTemplate(tpl, selectedEnquiry);
    setComposeSubject(built.subject || "");
    setComposeBody(textToHtml(built.body || ""));
    setComposeCourse(built.course || "");
    setComposeTemplateName(`(Copy) ${tpl?.template_name || "New Template"}`);

    setAiTplSuggest("");
    setAiTplError("");
    setLoadingAiTpl(false);
    // eslint-disable-next-line
  }, [composeUseTemplateId]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      alert("Copied to clipboard.");
    } catch (e) {
      console.error("copy failed", e);
      alert("Copy failed in this browser.");
    }
  };

  const handleAiSuggestFromTemplate = async () => {
    setAiTplError("");
    setAiTplSuggest("");

    if (!selectedEnquiry) {
      setAiTplError("No enquiry selected.");
      return;
    }
    if (!composeUseTemplateId) {
      setAiTplError("Please choose a template first.");
      return;
    }

    const tpl = findTemplateById(composeUseTemplateId);
    const tplDescRaw = tpl?.description || "";
    const tplDesc = replaceTokens(tplDescRaw, selectedEnquiry).trim();

    if (!tplDesc) {
      setAiTplError("Selected template has no description.");
      return;
    }

    setLoadingAiTpl(true);
    try {
      const res = await axios.post(ENDPOINT_AI_TEMPLATE, {
        name: selectedEnquiry?.name || "",
        description: tplDesc,
      });

      if (res.data?.success) {
        setAiTplSuggest(res.data.reply || "");
      } else {
        setAiTplError(res.data?.message || "Failed to generate AI suggestion.");
      }
    } catch (err) {
      console.error("generateAiTemplate error:", err?.response?.data || err);
      setAiTplError(
        err?.response?.data?.message ||
          "Server error. Failed to generate AI suggestion.",
      );
    } finally {
      setLoadingAiTpl(false);
    }
  };

  const applyAiSuggestionToBody = () => {
    if (!aiTplSuggest || !aiTplSuggest.trim()) return;
    setComposeBody(textToHtml(aiTplSuggest));
  };

  const saveTemplateUsingBackend = async () => {
    const templateName = composeTemplateName.trim();
    const course = composeCourse.trim();
    const subject = composeSubject.trim();
    const emailDesc = composeBody;

    if (!templateName || !course || !subject || !emailDesc) {
      alert("Template name, course, subject, and description are required.");
      return { ok: false };
    }

    const fd = new FormData();
    fd.append("templateName", templateName);
    fd.append("course", course);
    fd.append("subject", subject);
    fd.append("emailDesc", emailDesc);

    fd.append("iscopy", "1");

    composeFiles.forEach((file) => fd.append("attachments", file, file.name));

    try {
      const res = await axios.post(`${API}/saveEmailTemplate`, fd);
      if (res.data?.success) {
        return { ok: true, template_id: res.data?.template_id };
      }
      return {
        ok: false,
        message: res.data?.message || "Failed to save template.",
      };
    } catch (err) {
      console.error("saveEmailTemplate error:", err?.response?.data || err);
      return {
        ok: false,
        message:
          err?.response?.data?.message ||
          "Server error. Failed to save template.",
      };
    }
  };

  const handleSaveOnly = async () => {
    setSavingOnly(true);

    const saved = await saveTemplateUsingBackend();
    if (!saved.ok) {
      alert(saved.message || "Failed to save template.");
      setSavingOnly(false);
      return { ok: false };
    }

    const freshList = await fetchEmailTemplates();

    let template_id =
      saved.template_id != null ? Number(saved.template_id) : null;
    if (!Number.isFinite(template_id)) template_id = null;

    if (!template_id) {
      const name = composeTemplateName.trim();
      const sub = composeSubject.trim();
      const course = composeCourse.trim();

      const candidates = (freshList || []).filter(
        (t) =>
          String(t.template_name || "").trim() === name &&
          String(t.email_subject || "").trim() === sub &&
          String(t.course || "").trim() === course,
      );

      if (candidates.length > 0) {
        const best = candidates.sort(
          (a, b) => Number(b.template_id) - Number(a.template_id),
        )[0];
        template_id = best?.template_id ? Number(best.template_id) : null;
      }
    }

    if (!template_id) {
      alert(
        "Template saved, but template_id could not be detected. Please ensure backend returns insertId/template_id.",
      );
      setSavingOnly(false);
      return { ok: false };
    }

    alert("Template saved successfully!");
    setSavingOnly(false);

    return { ok: true, template_id };
  };

  const handleSaveAndSend = async () => {
    if (!selectedEnquiry?.id) return alert("No enquiry selected.");
    if (!window.confirm("Save as new template and send email now?")) return;

    const created_by = JSON.parse(localStorage.getItem("user"))?.user_name;
    if (!created_by) return alert("User not found. Please log in again.");

    setSavingAndSending(true);

    try {
      const savedResult = await handleSaveOnly();
      if (!savedResult?.ok || !savedResult?.template_id) {
        setSavingAndSending(false);
        return;
      }

      const template_id = Number(savedResult.template_id);

      const sendRes = await axios.post(`${API}/sendEmail`, {
        ids: [selectedEnquiry.id],
        template_id,
        created_by,
      });

      if (sendRes.data?.success) {
        alert("Email sending started.");
        setShowWriteModal(false);
        resetCompose();
        await fetchEnquiries();
      } else {
        alert(sendRes.data?.message || "Failed to send email.");
      }
    } catch (err) {
      console.error("handleSaveAndSend error:", err?.response?.data || err);
      alert(
        err?.response?.data?.message ||
          "Server error while saving/sending email.",
      );
    } finally {
      setSavingAndSending(false);
    }
  };

  const handleSendWithTemplate = (row) => {
    handleSendEmail(row.id);
    closeEmailMenu();
  };

  const parseAttachments = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v.filter(Boolean);
    const raw = String(v).trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch (e) {}
    if (raw.includes(",")) {
      return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [raw];
  };

  const countableEnquiries = useMemo(() => {
    return enquiries.filter((item) => item.archived !== 1);
  }, [enquiries]);

  const tagCountList = useMemo(() => {
    const map = new Map();

    countableEnquiries.forEach((item) => {
      const tagName = String(item?.label_name ?? "").trim() || "No Tag";
      const key = norm(tagName);
      const old = map.get(key) || {
        name: tagName,
        count: 0,
        color: "#6c757d",
      };

      old.count += 1;

      const matchedTag = labelList.find((x) => norm(x?.label_name) === key);
      if (matchedTag?.color) old.color = normalizeHex(matchedTag.color);

      map.set(key, old);
    });

    labelList.forEach((tag) => {
      const tagName = String(tag?.label_name ?? "").trim();
      if (!tagName) return;

      const key = norm(tagName);
      if (!map.has(key)) {
        map.set(key, {
          name: tagName,
          count: 0,
          color: normalizeHex(tag?.color) || "#6c757d",
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [countableEnquiries, labelList]);

  const sourceCountList = useMemo(() => {
    const map = new Map();

    countableEnquiries.forEach((item) => {
      const sourceName = String(item?.source ?? "").trim() || "Unknown";
      const key = norm(sourceName);
      const old = map.get(key) || {
        name: sourceName,
        count: 0,
        color: "#6c757d",
      };

      old.count += 1;

      const matchedSource = sourceList.find((x) => norm(x?.source) === key);
      if (matchedSource?.color) old.color = normalizeHex(matchedSource.color);

      map.set(key, old);
    });

    sourceList.forEach((src) => {
      const sourceName = String(src?.source ?? "").trim();
      if (!sourceName) return;

      const key = norm(sourceName);
      if (!map.has(key)) {
        map.set(key, {
          name: sourceName,
          count: 0,
          color: normalizeHex(src?.color) || "#6c757d",
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [countableEnquiries, sourceList]);

  const totalDashboardCount = useMemo(() => {
    return countableEnquiries.length;
  }, [countableEnquiries]);

  const getTagBadgeColorByName = (tagName) => {
    const found = labelList.find((x) => norm(x?.label_name) === norm(tagName));
    return accessibleBg(normalizeHex(found?.color) || "#1a3483");
  };

  const activeAdvanceFilterCount = selectedTags.length + selectedSources.length;

  const isAllChecked =
    sortedFilteredData.length > 0 &&
    selectedIds.length === sortedFilteredData.length;

  return (
    <Container
      fluid
      className={`min-vh-100 p-3 p-md-4 enq-page ${isDark ? "dark-theme" : "light-theme"}`}
    >
      <NavMenu />

      <div className="enqp-hero enqp-fade">
        <div className="enqp-hero-text">
          <h1 className="enqp-hero-title">Enquiries</h1>
          <p className="enqp-hero-sub">
            Manage enquiries, templates and replies efficiently.
          </p>
          <div className="enqp-hero-stats">
            <span className="enqp-hero-stat">
              <b>{sortedFilteredData.length}</b> showing
            </span>
            <span className="enqp-hero-stat">
              <b>{counts.reply}</b> yet to reply
            </span>
          </div>
        </div>

        <div className="enqp-hero-actions">
          <Dropdown>
            <Dropdown.Toggle className="enqp-hero-btn" variant="">
              <MdEmail />
              Templates
            </Dropdown.Toggle>

            <Dropdown.Menu align="end" className={isDark ? "dropdown-menu-dark" : ""}>
              <Dropdown.Header>Email</Dropdown.Header>
              <Dropdown.Item onClick={() => setShowTemplateModal(true)}>
                View Email Template
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowModal(true)}>
                <MdEmail className="me-2" />
                Create Email Template
              </Dropdown.Item>

              <Dropdown.Divider />

              <Dropdown.Header>WhatsApp</Dropdown.Header>
              <Dropdown.Item onClick={() => setShowWhatsappTemplateModal(true)}>
                View WhatsApp Template
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowWhatsappModal(true)}>
                <FaWhatsapp className="me-2" />
                Create WhatsApp Template
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <button
            type="button"
            className="enqp-hero-count"
            onClick={() => setShowCountSidebar(true)}
          >
            <BiSolidDashboard />
            <span>Total Counts</span>
            <strong>{totalDashboardCount}</strong>
          </button>
        </div>
      </div>

      {/* Filters */}
      <Card
        className={`enq-card enqp-card enqp-fade ${
          isDark ? "bg-dark text-white border-secondary" : ""
        }`}
        style={{ "--d": "80ms" }}
      >
        <Card.Body className="p-3 p-md-3">
          <Row className="g-2 align-items-center">
            <Col xs={12} lg={3}>
              <div className="enqp-search">
                <MdSearch className="enqp-search-icon" />
                <Form.Control
                  placeholder="Search name, phone, email, country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Search enquiries"
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="enqp-search-clear"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </Col>

            <Col xs={12} lg={3}>
              {(() => {
                const selectedEmailTpl = templateIdForSending
                  ? findTemplateById(templateIdForSending)
                  : null;
                const selectedWaTpl = whatsappTemplateIdForSending
                  ? findWhatsappTemplateById(whatsappTemplateIdForSending)
                  : null;
                const emailLabel =
                  selectedEmailTpl &&
                  (selectedEmailTpl.template_name ||
                    selectedEmailTpl.course ||
                    `Template ${selectedEmailTpl.template_id}`);
                const waLabel =
                  selectedWaTpl &&
                  (selectedWaTpl.template_name ||
                    selectedWaTpl.course ||
                    `Template ${selectedWaTpl.template_id}`);

                return (
                  <Button
                    className="enq-template-toggle enqp-template w-100"
                    onClick={() => setShowTemplatePickerModal(true)}
                  >
                    <span className="enq-template-toggle-label">
                      <MdEmail />
                      Template
                    </span>
                    <span className="enq-template-toggle-chips">
                      {emailLabel && (
                        <span className="enq-template-chip email" title={emailLabel}>
                          <MdEmail size={11} className="me-1" />
                          {emailLabel}
                        </span>
                      )}
                      {waLabel && (
                        <span className="enq-template-chip whatsapp" title={waLabel}>
                          <FaWhatsapp size={11} className="me-1" />
                          {waLabel}
                        </span>
                      )}
                      {!emailLabel && !waLabel && (
                        <span className="enq-template-toggle-placeholder">Choose...</span>
                      )}
                    </span>
                  </Button>
                );
              })()}
            </Col>

            <Col xs={12} lg={6} className="d-flex justify-content-lg-end">
              <ButtonGroup className="enq-filters gap-2">
                <Button
                  variant=""
                  className={`enq-filter-btn enqp-filter enqp-red ${showNoResponseOnly ? "active" : ""}`}
                  onClick={() => {
                    const toggled = !showNoResponseOnly;
                    setShowNoResponseOnly(toggled);
                    setShowSentOnly(false);
                    setShowStarOnly(false);
                  }}
                >
                  <TbCancel className="me-2" />
                  Yet to Reply
                  <Badge bg="danger" className="ms-2 enq-badge">
                    {counts.reply}
                  </Badge>
                </Button>

                <Button
                  variant=""
                  className={`enq-filter-btn enqp-filter enqp-amber ${showStarOnly ? "active" : ""}`}
                  onClick={() => {
                    const toggled = !showStarOnly;
                    setShowStarOnly(toggled);
                    setShowSentOnly(false);
                    setShowNoResponseOnly(false);
                  }}
                >
                  <FaRegStar className="me-2" />
                  Star List
                  <Badge bg="warning" className="ms-2 enq-badge">
                    {counts.star}
                  </Badge>
                </Button>

                <Button
                  variant=""
                  className={`enq-filter-btn enqp-filter enqp-green ${showSentOnly ? "active" : ""}`}
                  onClick={() => {
                    const toggled = !showSentOnly;
                    setShowSentOnly(toggled);
                    setShowStarOnly(false);
                    setShowNoResponseOnly(false);
                  }}
                >
                  <MdOutlineMarkEmailRead className="me-2" />
                  Sent
                  <Badge bg="success" className="ms-2 enq-badge">
                    {counts.sent}
                  </Badge>
                </Button>

                <Button
                  variant=""
                  className={`enq-filter-btn enqp-filter enqp-slate ${showArchivedOnly ? "active" : ""}`}
                  onClick={() => {
                    const toggled = !showArchivedOnly;
                    setShowArchivedOnly(toggled);
                    setShowSentOnly(false);
                    setShowStarOnly(false);
                    setShowNoResponseOnly(false);
                  }}
                >
                  <MdArchive className="me-2" />
                  Archived
                  <Badge bg="secondary" className="ms-2 enq-badge">
                    {counts.archived}
                  </Badge>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <div className="enq-actions-row">
            <div className="enq-left-actions">
              <Button
                size="sm"
                variant=""
                className="enq-action-btn enqp-btn enqp-btn-primary"
                onClick={() => setShowNewEnquiry(true)}
              >
                <FaIdCard className="me-2" />
                New Enquiry
              </Button>

              <span className="enq-toolbar-divider" />

              <Button
                size="sm"
                variant=""
                className="enq-action-btn enqp-btn enqp-btn-outline"
                onClick={() => setShowDateModal(true)}
              >
                <FaCalendarAlt className="me-2" />
                Filter Date
              </Button>

              <Button
                size="sm"
                variant=""
                className="enq-action-btn enqp-btn enqp-btn-outline"
                onClick={handleExportSelectedToExcel}
                disabled={selectedIds.length === 0}
              >
                <RiFileExcel2Fill className="me-2" />
                Export Selected ({selectedIds.length})
              </Button>
              <Button
                size="sm"
                variant=""
                className={`enq-action-btn enqp-btn enqp-btn-outline ${
                  activeAdvanceFilterCount > 0 ? "active" : ""
                }`}
                onClick={() => setShowAdvanceFilterModal(true)}
              >
                <FaFilter className="me-2" />
                Advance Filters
                {activeAdvanceFilterCount > 0 && (
                  <Badge bg="light" text="dark" className="ms-2">
                    {activeAdvanceFilterCount}
                  </Badge>
                )}
              </Button>
            </div>

            <div className="enq-right-actions">
              <Button
                variant=""
                className="enq-action-btn enqp-btn enqp-btn-ghost"
                onClick={clearSort}
              >
                Clear Sort
              </Button>

              <span className="enq-toolbar-divider" />

              <Button
                variant=""
                className="enq-action-btn enqp-btn enqp-btn-danger"
                onClick={handleDeleteSelected}
                disabled={selectedIds.length === 0}
              >
                <MdDelete className="me-2" />
                Delete ({selectedIds.length})
              </Button>

              <Button
                variant=""
                className="enq-action-btn enqp-btn enqp-btn-send"
                onClick={handleSendBulkEmail}
                disabled={!templateIdForSending || selectedIds.length === 0}
              >
                <BsFillSendCheckFill className="me-2" />
                Send Emails
              </Button>

              <Button
                variant=""
                className="enq-action-btn enqp-btn enqp-btn-wa"
                onClick={handleSendBulkWhatsapp}
                disabled={
                  sendingBulkWhatsapp ||
                  !whatsappTemplateIdForSending ||
                  selectedIds.length === 0
                }
                title="Only sends to selected enquiries whose source is WhatsApp"
              >
                {sendingBulkWhatsapp ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Sending {bulkWhatsappProgress?.done ?? 0}/{bulkWhatsappProgress?.total ?? 0}...
                  </>
                ) : (
                  <>
                    <FaWhatsapp className="me-2" />
                    Send WhatsApp
                  </>
                )}
              </Button>

              <span className="enq-toolbar-divider" />

              <Button
                variant=""
                className="enq-action-btn enqp-btn enqp-btn-ghost"
                onClick={handleRefresh}
              >
                <IoMdRefresh className="me-2" />
                Refresh
              </Button>
            </div>
          </div>

          {selectedRange && (
            <div
              className={`small mt-2 ${isDark ? "text-white-50" : "text-muted"}`}
            >
              {selectedRange}
            </div>
          )}

          {activeAdvanceFilterCount > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {selectedTags.map((tag) => {
                const color = getTagBadgeColorByName(tag);
                const count =
                  tagCountList.find((x) => norm(x.name) === norm(tag))?.count ||
                  0;

                return (
                  <Badge
                    key={`tag-${tag}`}
                    className="px-2 py-2"
                    style={{
                      backgroundColor: color,
                      color: getTextColor(color),
                    }}
                  >
                    Tag: {tag} ({count})
                  </Badge>
                );
              })}
              {selectedSources.map((source) => {
                const color = getSourceColor(source);
                const count =
                  sourceCountList.find((x) => norm(x.name) === norm(source))
                    ?.count || 0;

                return (
                  <Badge
                    key={`source-${source}`}
                    className="px-2 py-2"
                    style={{
                      backgroundColor: color,
                      color: getTextColor(color),
                    }}
                  >
                    Source: {source} ({count})
                  </Badge>
                );
              })}
              <Button
                size="sm"
                variant={isDark ? "outline-light" : "outline-secondary"}
                onClick={clearAdvanceFilters}
              >
                Clear Advance Filters
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Table */}
      <Card
        className={`enq-card enqp-card enqp-fade mt-3 ${
          isDark ? "bg-dark text-white border-secondary" : ""
        }`}
        style={{ "--d": "160ms" }}
      >
        <Card.Body className="p-0">
          {loading ? (
            <div className="enqp-skel-wrap" aria-busy="true" aria-label="Loading enquiries">
              {Array.from({ length: 6 }, (_, i) => (
                <span key={i} className="enqp-skel" />
              ))}
            </div>
          ) : sortedFilteredData.length === 0 ? (
            <div className="enqp-empty">
              <span className="enqp-empty-icon">
                <MdInbox />
              </span>
              <div className="enqp-empty-title">No enquiries found</div>
              <div className={isDark ? "text-white-50" : "text-muted"}>
                Try adjusting filters or refresh the list.
              </div>
              <div className="mt-2">
                <Button className="enqp-btn enqp-btn-primary" variant="" onClick={handleRefresh}>
                  <IoMdRefresh className="me-2" />
                  Refresh
                </Button>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table
                bordered={false}
                hover
                responsive="md"
                className={`mb-0 enq-table ${isDark ? "table-dark" : ""}`}
              >
                <thead className={`enq-thead ${isDark ? "table-dark" : ""}`}>
                  <tr>
                    <th style={{ width: 46 }} className="text-center">
                      <Form.Check
                        type="checkbox"
                        className="enq-checkbox"
                        checked={isAllChecked}
                        onChange={(e) => {
                          if (e.target.checked)
                            setSelectedIds(
                              sortedFilteredData.map((row) => row.id),
                            );
                          else setSelectedIds([]);
                        }}
                      />
                    </th>

                    <th
                      role="button"
                      title="Sort by row number"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("row_no")}
                    >
                      No{" "}
                      <span className="ms-1" style={sortIndicatorStyle}>
                        {sortIndicator("row_no")}
                      </span>
                    </th>

                    <th
                      role="button"
                      title="Sort by date"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("submission_date")}
                    >
                      Date{" "}
                      <span className="ms-1" style={sortIndicatorStyle}>
                        {sortIndicator("submission_date")}
                      </span>
                    </th>

                    <th
                      role="button"
                      title="Sort by name"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("name")}
                    >
                      Name{" "}
                      <span className="ms-1" style={sortIndicatorStyle}>
                        {sortIndicator("name")}
                      </span>
                    </th>

                    <th
                      className="hide-md"
                      role="button"
                      title="Sort by phone"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("phone_number")}
                    >
                      Phone{" "}
                      <span className="ms-1" style={sortIndicatorStyle}>
                        {sortIndicator("phone_number")}
                      </span>
                    </th>

                    <th
                      className="hide-lg"
                      role="button"
                      title="Sort by email"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("email")}
                    >
                      Email{" "}
                      <span className="ms-1" style={sortIndicatorStyle}>
                        {sortIndicator("email")}
                      </span>
                    </th>

                    <th
                      className="hide-md"
                      role="button"
                      title="Sort by country"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("country")}
                    >
                      Country{" "}
                      <span className="ms-1" style={sortIndicatorStyle}>
                        {sortIndicator("country")}
                      </span>
                    </th>

                    <th
                      className="hide-lg"
                      role="button"
                      title="Sort by details"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("enquiry")}
                    >
                      Details{" "}
                      <span className="ms-1" style={sortIndicatorStyle}>
                        {sortIndicator("enquiry")}
                      </span>
                    </th>

                    <th
                      className="hide-lg"
                      role="button"
                      title="Sort by tag"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("label_name")}
                    >
                      Tag{" "}
                      <span className="ms-1" style={sortIndicatorStyle}>
                        {sortIndicator("label_name")}
                      </span>
                    </th>

                    <th
                      className="text-center"
                      role="button"
                      title="Sort by source"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("source")}
                    >
                      Source{" "}
                      <span className="ms-1" style={sortIndicatorStyle}>
                        {sortIndicator("source")}
                      </span>
                    </th>

                    <th
                      className="text-center"
                      role="button"
                      title="Sort by status"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("status")}
                    >
                      Status{" "}
                      <span className="ms-1" style={sortIndicatorStyle}>
                        {sortIndicator("status")}
                      </span>
                    </th>

                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedData.map((row, index) => (
                    <tr
                      key={row.id}
                      className={selectedIds.includes(row.id) ? "enqp-row-selected" : ""}
                      style={row.archived === 1 ? { fontStyle: "italic" } : undefined}
                    >
                      <td className="text-center align-middle">
                        <Form.Check
                          type="checkbox"
                          className="enq-checkbox"
                          checked={selectedIds.includes(row.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked)
                              setSelectedIds((prev) => [...prev, row.id]);
                            else
                              setSelectedIds((prev) =>
                                prev.filter((id) => id !== row.id),
                              );
                          }}
                        />
                      </td>

                      <td className="align-middle">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>

                      <td className="align-middle">
                        {(() => {
                          const when = splitDateTime(formatDateTime(row.submission_date));
                          return (
                            <>
                              <div className="enqp-date">{when.date}</div>
                              <div className="enqp-time">{when.time}</div>
                            </>
                          );
                        })()}
                      </td>

                      <td className="align-middle">
                        <div className="enqp-person">
                          <Avatar name={row.name} size={34} />
                          <span className="enqp-person-name">{row.name}</span>
                        </div>
                      </td>

                      <td className="align-middle">
                        {formatPhoneForDisplay(row.phone_number, row.country, row.source, row.conversation_id)}
                      </td>

                      <td className="align-middle">
                        <span>{(row.email || "-").toLowerCase()}</span>
                      </td>

                      <td className="align-middle">
                        {row.country
                          ? row.country
                              .toLowerCase()
                              .replace(/\b\w/g, (c) => c.toUpperCase())
                          : "-"}
                      </td>

                      <td className="align-middle enqp-details">
                        {row.enquiry ? (
                          <span title={row.enquiry}>
                            {row.enquiry.length > 60
                              ? row.enquiry.substring(0, 60) + "..."
                              : row.enquiry}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* ✅ TAG PILL WITH DB COLOR */}
                      <td className="align-middle">
                        {row.label_name ? (
                          <span
                            className="enq-tag-pill"
                            title={
                              row.color
                                ? `Color: ${normalizeHex(row.color)}`
                                : "No tag color"
                            }
                            style={{
                              backgroundColor: getTagColor(row),
                              color: row.color
                                ? getTextColor(getTagColor(row))
                                : isDark
                                  ? "#e5e7eb"
                                  : "#111827",
                              border: isDark
                                ? "1px solid rgba(255,255,255,0.18)"
                                : "1px solid rgba(0,0,0,0.08)",
                            }}
                          >
                            <span
                              className="enq-tag-dot"
                              style={{
                                backgroundColor: row.color
                                  ? normalizeHex(row.color)
                                  : isDark
                                    ? "rgba(255,255,255,0.35)"
                                    : "#adb5bd",
                              }}
                            />
                            {(row.label_name || "-").toUpperCase()}
                          </span>
                        ) : (
                          <span
                            className={isDark ? "text-white-50" : "text-muted"}
                          >
                            -
                          </span>
                        )}
                      </td>

                      <td className="align-middle text-center">
                        <span
                          className="enq-chip"
                          style={{
                            backgroundColor: getSourceColor(row.source),
                            color: getTextColor(getSourceColor(row.source)),
                          }}
                        >
                          {row.source || "Unknown"}
                        </span>
                      </td>

                      <td className="align-middle text-center">
                        <span
                          className="enq-chip"
                          style={{
                            backgroundColor: getStatusColor(
                              getStatusLabel(row.status),
                            ),
                            color: getTextColor(
                              getStatusColor(getStatusLabel(row.status)),
                            ),
                          }}
                        >
                          {getStatusLabel(row.status)}
                        </span>
                      </td>

                      <td className="align-middle">
                        {/* (your actions unchanged) */}
                        <div className="enq-action-icons">
                          <div className="enq-icon-btn-wrap">
                            {row.has_new_message ? (
                              <span
                                className="enq-new-message-badge"
                                title="New WhatsApp message"
                              >
                                New
                              </span>
                            ) : null}
                            <button
                              className="enq-icon-btn"
                              title="View Details"
                              onClick={() => {
                                setSelectedEnquiry(row);
                                setShowDetailsModal(true);
                                handleMarkWhatsappSeen(row);
                              }}
                            >
                              <IoMdEye size={18} />
                            </button>
                          </div>

                          <button
                            className="enq-icon-btn"
                            title="Edit"
                            onClick={() => {
                              setEditingEnquiry(row);
                              setShowNewEnquiry(true);
                            }}
                          >
                            <FaEdit size={16} />
                          </button>

                          <OverlayTrigger
                            trigger="click"
                            placement="left"
                            rootClose
                            show={showEmailMenuId === row.id}
                            onToggle={(next) =>
                              setShowEmailMenuId(next ? row.id : null)
                            }
                            overlay={
                              <Popover
                                id={`email-popover-${row.id}`}
                                className={
                                  isDark
                                    ? "bg-dark text-white border-secondary"
                                    : ""
                                }
                              >
                                <Popover.Body
                                  className="p-2"
                                  style={{ fontFamily: "Arimo-Regular" }}
                                >
                                  <div className="d-grid gap-2">
                                    <Button
                                      size="sm"
                                      variant={
                                        isDark
                                          ? "outline-light"
                                          : "outline-primary"
                                      }
                                      onClick={() =>
                                        handleSendWithTemplate(row)
                                      }
                                      disabled={
                                        row.archived === 1 ||
                                        !templateIdForSending
                                      }
                                    >
                                      <MdOutlineMarkEmailRead className="me-2" />
                                      Send Template
                                    </Button>

                                    <Button
                                      size="sm"
                                      variant={
                                        isDark
                                          ? "outline-light"
                                          : "outline-secondary"
                                      }
                                      onClick={() => handleWriteEmail(row)}
                                      disabled={row.archived === 1}
                                    >
                                      <FaEdit className="me-2" />
                                      Write Email (Use Template)
                                    </Button>
                                  </div>

                                  {!templateIdForSending && (
                                    <div
                                      className={`mt-2 small ${
                                        isDark ? "text-white-50" : "text-muted"
                                      }`}
                                    >
                                      Select a template to auto-fill. You can
                                      still edit and save as new template.
                                    </div>
                                  )}
                                </Popover.Body>
                              </Popover>
                            }
                          >
                            <button
                              className="enq-icon-btn"
                              title="Email Options"
                              onClick={() => openEmailMenu(row.id)}
                              disabled={row.archived === 1}
                            >
                              <FaEnvelope size={16} />
                            </button>
                          </OverlayTrigger>

                          <button
                            className="enq-icon-btn"
                            title={row.archived === 1 ? "Unarchive" : "Archive"}
                            onClick={() => toggleArchive(row.id, row.archived)}
                          >
                            {row.archived === 1 ? (
                              <MdUnarchive size={18} />
                            ) : (
                              <MdArchive size={18} />
                            )}
                          </button>

                          <button
                            className="enq-icon-btn"
                            title={row.starred === 1 ? "Unstar" : "Star"}
                            onClick={() => toggleStar(row.id, row.starred)}
                          >
                            <FaStar
                              size={16}
                              style={{
                                color:
                                  row.starred === 1 ? "#ffc107" : "#b5b5b5",
                              }}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {!loading && totalPages > 1 && (
        <div className="enqp-pager">
          <div className="enqp-pager-info">
            Showing{" "}
            <strong>
              {(currentPage - 1) * itemsPerPage + 1}–
              {Math.min(currentPage * itemsPerPage, sortedFilteredData.length)}
            </strong>{" "}
            of <strong>{sortedFilteredData.length}</strong> enquiries
          </div>
          <Pagination size="sm">
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              const isCurrent = page === currentPage;
              const isNear = Math.abs(page - currentPage) <= 2;
              const isEdge = page === 1 || page === totalPages;

              if (isCurrent || isNear || isEdge) {
                return (
                  <Pagination.Item
                    key={page}
                    active={isCurrent}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              }

              if (
                (page === currentPage - 3 && page !== 2) ||
                (page === currentPage + 3 && page !== totalPages - 1)
              ) {
                return (
                  <Pagination.Ellipsis key={`ellipsis-${page}`} disabled />
                );
              }
              return null;
            })}
            <Pagination.Next
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* Create Template Modal */}
      <EmailEditorModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={() => {
          setShowModal(false);
          fetchEmailTemplates();
        }}
      />

      {/* Create WhatsApp Template Modal */}
      <WhatsappTemplateEditorModal
        show={showWhatsappModal}
        onClose={() => setShowWhatsappModal(false)}
        mode="create"
        onSaved={() => {
          setShowWhatsappModal(false);
        }}
      />

      {/* New Enquiry */}
      <NewEnquiryModal
        show={showNewEnquiry}
        onClose={() => {
          setShowNewEnquiry(false);
          setEditingEnquiry(null);
        }}
        onSubmit={() => {
          fetchEnquiries();
          setEditingEnquiry(null);
        }}
        enquiry={editingEnquiry}
      />

      {/* Template Picker Modal */}
      <Modal
        show={showTemplatePickerModal}
        onHide={() => setShowTemplatePickerModal(false)}
        centered
        className={isDark ? "text-white" : ""}
      >
        <Modal.Header
          closeButton
          className={isDark ? "bg-dark text-white border-secondary" : ""}
        >
          <Modal.Title>Choose a Template</Modal.Title>
        </Modal.Header>

        <Modal.Body className={isDark ? "bg-dark text-white" : ""}>
          <div className="enq-template-tabs mb-3">
            <button
              type="button"
              className={`enq-template-tab enq-template-tab-email ${templateMenuChannel === "email" ? "active" : ""}`}
              onClick={() => setTemplateMenuChannel("email")}
            >
              <MdEmail className="me-1" />
              Email
            </button>
            <button
              type="button"
              className={`enq-template-tab enq-template-tab-whatsapp ${templateMenuChannel === "whatsapp" ? "active" : ""}`}
              onClick={() => setTemplateMenuChannel("whatsapp")}
            >
              <FaWhatsapp className="me-1" />
              WhatsApp
            </button>
          </div>

          <div className="enq-template-list enq-template-list-modal">
            {templateMenuChannel === "email" ? (
              <>
                <button
                  type="button"
                  className={`enq-template-row ${!templateIdForSending ? "active" : ""}`}
                  onClick={() => selectEmailTemplateForSending("")}
                >
                  {!templateIdForSending && <FaCheck className="me-2" size={12} />}
                  No email template
                </button>
                {emailTemplates.length === 0 && (
                  <div className="enq-template-empty">No email templates yet.</div>
                )}
                {emailTemplates.map((t) => {
                  const active = String(templateIdForSending) === String(t.template_id);
                  return (
                    <button
                      type="button"
                      key={t.template_id}
                      className={`enq-template-row ${active ? "active" : ""}`}
                      onClick={() => selectEmailTemplateForSending(t.template_id)}
                    >
                      {active && <FaCheck className="me-2" size={12} />}
                      {t.template_name || t.course || `Template ${t.template_id}`}
                    </button>
                  );
                })}
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={`enq-template-row ${!whatsappTemplateIdForSending ? "active" : ""}`}
                  onClick={() => selectWhatsappTemplateForSending("")}
                >
                  {!whatsappTemplateIdForSending && <FaCheck className="me-2" size={12} />}
                  No WhatsApp template
                </button>
                {whatsappTemplates.length === 0 && (
                  <div className="enq-template-empty">No WhatsApp templates yet.</div>
                )}
                {whatsappTemplates.map((t) => {
                  const active = String(whatsappTemplateIdForSending) === String(t.template_id);
                  return (
                    <button
                      type="button"
                      key={t.template_id}
                      className={`enq-template-row ${active ? "active" : ""}`}
                      onClick={() => selectWhatsappTemplateForSending(t.template_id)}
                    >
                      {active && <FaCheck className="me-2" size={12} />}
                      {t.template_name || t.course || `Template ${t.template_id}`}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className={isDark ? "bg-dark border-secondary" : ""}>
          <Button
            variant={isDark ? "outline-light" : "outline-secondary"}
            onClick={() => setShowTemplatePickerModal(false)}
          >
            Done
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Date Modal */}
      <Modal
        show={showDateModal}
        onHide={() => setShowDateModal(false)}
        className={isDark ? "text-white" : ""}
      >
        <Modal.Header
          closeButton
          className={isDark ? "bg-dark text-white border-secondary" : ""}
        >
          <Modal.Title>Select Date Range</Modal.Title>
        </Modal.Header>

        <Modal.Body className={isDark ? "bg-dark text-white" : ""}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className={isDark ? "text-white" : ""}>
                From
              </Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={isDark ? "bg-dark text-white border-secondary" : ""}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className={isDark ? "text-white" : ""}>To</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={isDark ? "bg-dark text-white border-secondary" : ""}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className={isDark ? "bg-dark border-secondary" : ""}>
          <Button
            variant={isDark ? "secondary" : "light"}
            onClick={() => setShowDateModal(false)}
          >
            Close
          </Button>
          <Button
            variant={isDark ? "light" : "primary"}
            onClick={() => setShowDateModal(false)}
          >
            Apply Filter
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Advance Filter Modal */}
      <Modal
        show={showAdvanceFilterModal}
        onHide={() => setShowAdvanceFilterModal(false)}
        size="lg"
        centered
        className={isDark ? "text-white" : ""}
      >
        <Modal.Header
          closeButton
          className={isDark ? "bg-dark text-white border-secondary" : ""}
        >
          <Modal.Title>Advance Filters</Modal.Title>
        </Modal.Header>

        <Modal.Body className={isDark ? "bg-dark text-white" : ""}>
          <Row className="g-3">
            <Col md={4}>
              <div
                className={`rounded-3 p-2 h-100 ${
                  isDark ? "border border-secondary" : "border"
                }`}
              >
                <Button
                  type="button"
                  variant={
                    advanceFilterTab === "tag"
                      ? "primary"
                      : isDark
                        ? "outline-light"
                        : "outline-secondary"
                  }
                  className="w-100 text-start mb-2 d-flex justify-content-between align-items-center"
                  onClick={() => setAdvanceFilterTab("tag")}
                >
                  <span>Tag</span>
                  {selectedTags.length > 0 && (
                    <Badge bg="light" text="dark">
                      {selectedTags.length}
                    </Badge>
                  )}
                </Button>

                <Button
                  type="button"
                  variant={
                    advanceFilterTab === "source"
                      ? "primary"
                      : isDark
                        ? "outline-light"
                        : "outline-secondary"
                  }
                  className="w-100 text-start d-flex justify-content-between align-items-center"
                  onClick={() => setAdvanceFilterTab("source")}
                >
                  <span>Source</span>
                  {selectedSources.length > 0 && (
                    <Badge bg="light" text="dark">
                      {selectedSources.length}
                    </Badge>
                  )}
                </Button>
              </div>
            </Col>

            <Col md={8}>
              <div
                className={`rounded-3 p-3 ${
                  isDark ? "border border-secondary" : "border"
                }`}
                style={{ maxHeight: 420, overflowY: "auto" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="fw-bold">
                    {advanceFilterTab === "tag"
                      ? "Select Tags"
                      : "Select Sources"}
                  </div>
                  <Button
                    size="sm"
                    variant={isDark ? "outline-light" : "outline-secondary"}
                    onClick={() =>
                      advanceFilterTab === "tag"
                        ? setSelectedTags([])
                        : setSelectedSources([])
                    }
                  >
                    Clear {advanceFilterTab === "tag" ? "Tags" : "Sources"}
                  </Button>
                </div>

                {advanceFilterTab === "tag" ? (
                  labelList.length === 0 ? (
                    <div className={isDark ? "text-white-50" : "text-muted"}>
                      No tag data found. Please create tags in Data Center
                      first.
                    </div>
                  ) : (
                    <Row className="g-2">
                      {labelList.map((tag) => {
                        const labelName = String(tag?.label_name ?? "").trim();
                        if (!labelName) return null;
                        const checked = selectedTags.includes(labelName);
                        const color = normalizeHex(tag?.color) || "#6c757d";

                        return (
                          <Col xs={12} sm={6} key={tag.label_id || labelName}>
                            <div
                              className={`rounded-3 p-2 ${
                                isDark ? "border border-secondary" : "border"
                              }`}
                            >
                              <Form.Check
                                type="checkbox"
                                id={`adv-tag-${tag.label_id || labelName}`}
                                checked={checked}
                                onChange={() =>
                                  toggleArrayValue(labelName, setSelectedTags)
                                }
                                label={
                                  <span className="d-inline-flex align-items-center gap-2">
                                    <span
                                      style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: 999,
                                        background: color,
                                      }}
                                    />
                                    {labelName}
                                  </span>
                                }
                              />
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )
                ) : sourceList.length === 0 ? (
                  <div className={isDark ? "text-white-50" : "text-muted"}>
                    No source data found. Please create sources in Data Center
                    first.
                  </div>
                ) : (
                  <Row className="g-2">
                    {sourceList.map((src) => {
                      const sourceName = String(src?.source ?? "").trim();
                      if (!sourceName) return null;
                      const checked = selectedSources.includes(sourceName);
                      const color = normalizeHex(src?.color) || "#6c757d";

                      return (
                        <Col xs={12} sm={6} key={src.source_id || sourceName}>
                          <div
                            className={`rounded-3 p-2 ${
                              isDark ? "border border-secondary" : "border"
                            }`}
                          >
                            <Form.Check
                              type="checkbox"
                              id={`adv-source-${src.source_id || sourceName}`}
                              checked={checked}
                              onChange={() =>
                                toggleArrayValue(sourceName, setSelectedSources)
                              }
                              label={
                                <span className="d-inline-flex align-items-center gap-2">
                                  <span
                                    style={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: 999,
                                      background: color,
                                    }}
                                  />
                                  {sourceName}
                                </span>
                              }
                            />
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className={isDark ? "bg-dark border-secondary" : ""}>
          <Button
            variant={isDark ? "outline-light" : "outline-secondary"}
            onClick={clearAdvanceFilters}
          >
            Clear All
          </Button>
          <Button
            variant={isDark ? "secondary" : "light"}
            onClick={() => setShowAdvanceFilterModal(false)}
          >
            Close
          </Button>
          <Button
            style={{ backgroundColor: "#1a3483", borderColor: "#1a3483" }}
            onClick={() => setShowAdvanceFilterModal(false)}
          >
            Apply Filter
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Total Counts Sidebar */}
      <Offcanvas
        show={showCountSidebar}
        onHide={() => setShowCountSidebar(false)}
        placement="end"
        className={isDark ? "bg-dark text-white" : ""}
      >
        <Offcanvas.Header
          closeButton
          className={isDark ? "border-secondary" : ""}
        >
          <Offcanvas.Title>
            <BiSolidDashboard className="me-2" />
            Total Counts
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <div className="enq-count-stats">
            <div className="enq-count-stat accent-blue">
              <div className="enq-count-stat-icon">
                <BiSolidDashboard />
              </div>
              <div>
                <div className="enq-count-stat-value">{totalDashboardCount}</div>
                <div className="enq-count-stat-label">Total Enquiries</div>
              </div>
            </div>

            <div className="enq-count-stat accent-magenta">
              <div className="enq-count-stat-icon">
                <FaFilter />
              </div>
              <div>
                <div className="enq-count-stat-value">{activeAdvanceFilterCount}</div>
                <div className="enq-count-stat-label">Active Filters</div>
              </div>
            </div>
          </div>

          <div className="enq-count-tabs">
            <button
              type="button"
              className={`enq-count-tab ${countSidebarTab === "tag" ? "active" : ""}`}
              onClick={() => setCountSidebarTab("tag")}
            >
              Tags
              <span className="enq-count-tab-badge">{tagCountList.length}</span>
            </button>

            <button
              type="button"
              className={`enq-count-tab ${countSidebarTab === "source" ? "active" : ""}`}
              onClick={() => setCountSidebarTab("source")}
            >
              Sources
              <span className="enq-count-tab-badge">{sourceCountList.length}</span>
            </button>
          </div>

          <div className="enq-count-hint">
            Click a {countSidebarTab === "tag" ? "tag" : "source"} below to filter the enquiry list by it.
          </div>

          {activeAdvanceFilterCount > 0 && (
            <button type="button" className="enq-count-clear" onClick={clearAdvanceFilters}>
              <FaTimes size={11} />
              Clear all active filters
            </button>
          )}

          <div className="d-grid gap-2">
            {(countSidebarTab === "tag" ? tagCountList : sourceCountList).map(
              (item) => {
                const isTag = countSidebarTab === "tag";
                const selected = isTag
                  ? selectedTags.map(norm).includes(norm(item.name))
                  : selectedSources.map(norm).includes(norm(item.name));

                return (
                  <div
                    key={`${countSidebarTab}-${item.name}`}
                    className={`enq-count-item ${selected ? "selected" : ""}`}
                    style={{ "--item-color": item.color }}
                    onClick={() => {
                      if (isTag) toggleArrayValue(item.name, setSelectedTags);
                      else toggleArrayValue(item.name, setSelectedSources);
                    }}
                  >
                    <div
                      className="enq-count-item-icon"
                      style={{
                        backgroundColor: accessibleBg(item.color),
                        color: getTextColor(accessibleBg(item.color)),
                      }}
                    >
                      {isTag ? <TbLabelImportantFilled /> : <MdSource />}
                    </div>

                    <div className="enq-count-item-info">
                      <div className="enq-count-item-name">{item.name}</div>
                      <div className="enq-count-item-type">{isTag ? "Tag" : "Source"}</div>
                    </div>

                    <div className="enq-count-item-value">
                      <div className="enq-count-item-number">{item.count}</div>
                      <div className="enq-count-item-suffix">enquiries</div>
                    </div>

                    {selected && <FaCheck className="enq-count-item-check" />}
                  </div>
                );
              },
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false);
          setAiReply("");
        }}
        size="lg"
        className={isDark ? "text-white" : ""}
      >
        <Modal.Header
          closeButton
          className={isDark ? "bg-dark text-white border-secondary" : ""}
        >
          <Modal.Title>Enquiry Details</Modal.Title>
        </Modal.Header>

        <Modal.Body className={isDark ? "bg-dark text-white" : ""}>
          {selectedEnquiry && (
            <Container fluid>
              <Row className="mb-3">
                <Col md={4} className="fw-bold">
                  Name:
                </Col>
                <Col md={8}>{selectedEnquiry.name}</Col>
              </Row>
              <Row className="mb-3">
                <Col md={4} className="fw-bold">
                  Phone:
                </Col>
                <Col md={8}>
                  {formatPhoneForDisplay(
                    selectedEnquiry.phone_number,
                    selectedEnquiry.country,
                    selectedEnquiry.source,
                    selectedEnquiry.conversation_id
                  )}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4} className="fw-bold">
                  Email:
                </Col>
                <Col md={8}>{selectedEnquiry.email}</Col>
              </Row>
              <Row className="mb-3">
                <Col md={4} className="fw-bold">
                  Country:
                </Col>
                <Col md={8}>{selectedEnquiry.country}</Col>
              </Row>
              <Row className="mb-3">
                <Col md={4} className="fw-bold">
                  Details:
                </Col>
                <Col md={8}>{selectedEnquiry.enquiry}</Col>
              </Row>
            </Container>
          )}

          {aiReply && (
            <div
              className={`mt-3 p-3 rounded ${
                isDark ? "border border-secondary" : "bg-light"
              }`}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className={`mb-0 ${isDark ? "text-white" : ""}`}>
                  AI-Generated Email Reply
                </h6>

                <Button
                  variant={isDark ? "light" : "primary"}
                  onClick={() => {}}
                  disabled
                  title="(Your existing sendAiEmail code is in your file; keep it if needed)"
                >
                  <FaPaperPlane className="me-2" />
                  Send Reply
                </Button>
              </div>

              <Form.Control
                as="textarea"
                rows={10}
                className={`${isDark ? "bg-dark text-white border-secondary" : ""}`}
                value={aiReply}
                onChange={(e) => setAiReply(e.target.value)}
                style={{ whiteSpace: "pre-wrap" }}
              />
            </div>
          )}

          {isWhatsappChannelSource(selectedEnquiry?.source) && (
            <div
              className={`mt-3 p-3 rounded ${
                isDark ? "border border-secondary" : "bg-light"
              }`}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className={`mb-0 ${isDark ? "text-white" : ""}`}>
                  WhatsApp Conversation
                </h6>
                <Button
                  size="sm"
                  variant={isDark ? "outline-light" : "outline-secondary"}
                  onClick={() => fetchWhatsappMessages(selectedEnquiry.id)}
                  disabled={loadingWhatsapp}
                  title="Manually reload (new messages now arrive automatically)"
                >
                  <IoMdRefresh size={16} />
                </Button>
              </div>

              {loadingWhatsapp && (
                <div className="d-flex align-items-center gap-2 py-3 justify-content-center">
                  <Spinner animation="border" size="sm" />
                  <span className={isDark ? "text-white-50" : "text-muted"}>
                    Loading conversation...
                  </span>
                </div>
              )}

              {!loadingWhatsapp && whatsappError && (
                <div className={`enq-wa-empty ${isDark ? "dark" : ""}`}>
                  <div className="enq-wa-empty-icon">
                    <BsChatSquareText />
                  </div>
                  <div className="enq-wa-empty-text">{whatsappError}</div>
                </div>
              )}

              {!loadingWhatsapp && !whatsappError && (
                <>
                  <div
                    ref={whatsappScrollContainerRef}
                    className="mb-3 px-1"
                    style={{
                      maxHeight: 260,
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    {whatsappMessages.length === 0 && (
                      <div
                        className={`small text-center py-2 ${
                          isDark ? "text-white-50" : "text-muted"
                        }`}
                      >
                        No messages yet.
                      </div>
                    )}

                    {whatsappMessages.map((msg) => {
                      const isStaff = msg.direction === "outbound";
                      const bodyText = msg.caption || msg.message_content || null;
                      return (
                        <div
                          key={msg.id}
                          className="d-flex"
                          style={{
                            justifyContent: isStaff
                              ? "flex-end"
                              : "flex-start",
                          }}
                        >
                          <div
                            className="px-3 py-2 rounded-3"
                            style={{
                              maxWidth: "75%",
                              backgroundColor: isStaff
                                ? "#0C7536"
                                : isDark
                                  ? "#6c7280"
                                  : "#ffffff",
                              color: isStaff
                                ? "#ffffff"
                                : isDark
                                  ? "#ffffff"
                                  : "#212529",
                              border: isStaff
                                ? "none"
                                : isDark
                                  ? "1px solid #8991a3"
                                  : "1px solid #7c8894",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            <WhatsappMessageMedia msg={msg} />
                            {bodyText && (
                              <div style={{ fontSize: "0.9rem" }}>
                                {bodyText}
                              </div>
                            )}
                            {!bodyText && !msg.media_url && (
                              <div style={{ fontSize: "0.9rem" }}>
                                {`[${msg.message_type || "media"}]`}
                              </div>
                            )}
                            {msg.timestamp && (
                              <div
                                className="text-end"
                                style={{
                                  fontSize: "0.75rem",
                                  marginTop: 2,
                                }}
                              >
                                {new Date(msg.timestamp).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {whatsappAttachment && (
                    <div
                      className={`d-flex align-items-center gap-2 mb-2 p-2 rounded ${
                        isDark ? "bg-secondary bg-opacity-25" : "bg-white border"
                      }`}
                    >
                      {whatsappAttachment.previewUrl ? (
                        <img
                          src={whatsappAttachment.previewUrl}
                          alt={whatsappAttachment.file.name}
                          style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }}
                        />
                      ) : (
                        <FaFileAlt size={22} className={isDark ? "text-white-50" : "text-secondary"} />
                      )}
                      <div className="flex-grow-1 small text-truncate">
                        {whatsappAttachment.file.name}
                        <span className={isDark ? "text-white-50" : "text-muted"}>
                          {" "}({(whatsappAttachment.file.size / 1024).toFixed(0)} KB)
                        </span>
                      </div>
                      {whatsappUploadProgress !== null ? (
                        <div style={{ width: 80 }}>
                          <div className="progress" style={{ height: 6 }}>
                            <div
                              className="progress-bar bg-success"
                              style={{ width: `${whatsappUploadProgress}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="link"
                          className="text-danger p-0"
                          onClick={handleRemoveWhatsappAttachment}
                          title="Remove attachment"
                        >
                          <FaTimes />
                        </Button>
                      )}
                    </div>
                  )}

                  {loadingWhatsappTemplateAttachments && (
                    <div className="d-flex align-items-center gap-2 mb-2 small text-muted">
                      <Spinner animation="border" size="sm" />
                      Fetching template attachment(s)...
                    </div>
                  )}

                  {whatsappTemplateAttachments.map((a) => (
                    <div
                      key={a.filename}
                      className={`d-flex align-items-center gap-2 mb-2 p-2 rounded ${
                        isDark ? "bg-secondary bg-opacity-25" : "bg-white border"
                      }`}
                    >
                      {a.previewUrl ? (
                        <img
                          src={a.previewUrl}
                          alt={a.file.name}
                          style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }}
                        />
                      ) : (
                        <FaFileAlt size={22} className={isDark ? "text-white-50" : "text-secondary"} />
                      )}
                      <div className="flex-grow-1 small text-truncate">
                        {a.file.name}
                        <span className={isDark ? "text-white-50" : "text-muted"}>
                          {" "}({(a.file.size / 1024).toFixed(0)} KB) · from template
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="link"
                        className="text-danger p-0"
                        onClick={() => handleRemoveWhatsappTemplateAttachment(a.filename)}
                        title="Remove this attachment"
                        disabled={sendingWhatsappReply}
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  ))}

                  {whatsappSendFailed && (
                    <div className="d-flex align-items-center gap-2 mb-2 small text-danger">
                      <span>Send failed.</span>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={handleSendWhatsappReply}
                        disabled={sendingWhatsappReply}
                      >
                        Retry
                      </Button>
                    </div>
                  )}

                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleWhatsappDrop}
                  >
                    <div className="enq-wa-template-wrap mb-2">
                      <BsChatSquareText className="enq-wa-template-icon" />
                      <Form.Select
                        value={whatsappTemplateIdForReply}
                        onChange={(e) => handleSelectWhatsappTemplate(e.target.value)}
                        disabled={sendingWhatsappReply}
                        className={`enq-wa-template-select ${isDark ? "bg-dark text-white border-secondary" : ""}`}
                      >
                        <option value="">Quick reply from a template...</option>
                        {whatsappTemplates.map((t) => (
                          <option key={t.template_id} value={t.template_id}>
                            {t.template_name ||
                              t.course ||
                              `Template ${t.template_id}`}
                          </option>
                        ))}
                      </Form.Select>
                    </div>

                    <div className={`enq-wa-composer ${isDark ? "dark" : ""}`}>
                      <button
                        type="button"
                        className="enq-wa-attach-btn"
                        onClick={() => whatsappFileInputRef.current?.click()}
                        disabled={sendingWhatsappReply}
                        title="Attach a file"
                      >
                        <FaPaperclip size={16} />
                      </button>

                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder={
                          whatsappAttachment
                            ? "Add a caption (optional)..."
                            : "Type a WhatsApp reply, or drop a file here..."
                        }
                        className={`enq-wa-textarea ${isDark ? "text-white" : ""}`}
                        value={whatsappReplyText}
                        onChange={(e) => {
                          setWhatsappReplyText(e.target.value);
                          // Free-editing the text after picking a template
                          // detaches the selector, same as email's compose
                          // modal treats manual edits as a one-off copy.
                          if (whatsappTemplateIdForReply) setWhatsappTemplateIdForReply("");
                        }}
                        disabled={sendingWhatsappReply}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendWhatsappReply();
                          }
                        }}
                      />

                      <button
                        type="button"
                        className="enq-wa-send-btn"
                        onClick={handleSendWhatsappReply}
                        disabled={
                          sendingWhatsappReply ||
                          loadingWhatsappTemplateAttachments ||
                          (!whatsappReplyText.trim() &&
                            !whatsappAttachment &&
                            whatsappTemplateAttachments.length === 0)
                        }
                      >
                        {sendingWhatsappReply ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FaPaperPlane size={15} />
                        )}
                      </button>
                    </div>
                    <input
                      ref={whatsappFileInputRef}
                      type="file"
                      hidden
                      onChange={(e) => handleWhatsappFileSelect(e.target.files?.[0])}
                    />
                  </div>
                  <div
                    className={`small mt-1 ${
                      isDark ? "text-white-50" : "text-muted"
                    }`}
                  >
                    Press Enter to send. Press Shift+Enter to insert a new line.
                  </div>
                </>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className={isDark ? "bg-dark border-secondary" : ""}>
          {selectedEnquiry?.status === "email sent" && (
            <Button variant="outline-primary" onClick={handleReviewEmail}>
              Review Email
            </Button>
          )}

          {/* <Button
            variant={isDark ? "light" : "primary"}
            onClick={handleAISupport}
            disabled={loadingAI}
          >
            {loadingAI ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Analyzing...
              </>
            ) : (
              "AI Support"
            )}
          </Button> */}

          <Button
            variant="secondary"
            onClick={() => {
              setShowDetailsModal(false);
              setAiReply("");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Review Modal */}
      <Modal
        show={showReviewModal}
        onHide={() => setShowReviewModal(false)}
        size="lg"
        className={isDark ? "text-white" : ""}
      >
        <Modal.Header
          closeButton
          className={isDark ? "bg-dark text-white border-secondary" : ""}
        >
          <Modal.Title>Review Email</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDark ? "bg-dark text-white" : ""}>
          {loadingReplyLogs ? (
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading email conversation...
            </div>
          ) : replyLogs.length === 0 ? (
            <div className={isDark ? "text-white-50" : "text-muted"}>
              No email logs found.
            </div>
          ) : (
            <div style={{ maxHeight: 460, overflowY: "auto" }}>
              {replyLogs.map((msg, idx) => (
                <div key={idx} className="mb-2">
                  <div className="small">
                    {formatDateTime(msg.created_at)}
                  </div>
                  <div
                    className="p-2 rounded"
                    style={{ background: isDark ? "#1f2a44" : "#e9f1ff" }}
                    dangerouslySetInnerHTML={{
                      __html: basicSanitize(msg.description || ""),
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className={isDark ? "bg-dark border-secondary" : ""}>
          <Button variant="secondary" onClick={() => setShowReviewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Template Preview */}
      <Modal
        show={showTemplateModal}
        onHide={() => {
          setShowTemplateModal(false);
          fetchEmailTemplates();
        }}
        size="xl"
        centered
        scrollable
        className={`tpv-modal ${isDark ? "tpv-modal-dark" : ""}`}
      >
        <Modal.Header closeButton className="tpv-modal-head">
          <span className="tpv-modal-icon email"><FaEnvelope /></span>
          <Modal.Title className="tpv-modal-title">Email Template Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tpv-modal-body">
          <EmailTemplate />
        </Modal.Body>
      </Modal>

      {/* WhatsApp Template Preview */}
      <Modal
        show={showWhatsappTemplateModal}
        onHide={() => {
          setShowWhatsappTemplateModal(false);
          fetchWhatsappTemplates();
        }}
        size="xl"
        centered
        scrollable
        className={`tpv-modal ${isDark ? "tpv-modal-dark" : ""}`}
      >
        <Modal.Header closeButton className="tpv-modal-head">
          <span className="tpv-modal-icon whatsapp"><FaWhatsapp /></span>
          <Modal.Title className="tpv-modal-title">WhatsApp Template Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tpv-modal-body">
          <WhatsappTemplate />
        </Modal.Body>
      </Modal>

      {/* Write Email */}
      <Modal
        show={showWriteModal}
        onHide={() => {
          setShowWriteModal(false);
          resetCompose();
        }}
        size="xl"
        centered
        className={isDark ? "text-white" : ""}
      >
        <Modal.Header
          closeButton
          className={isDark ? "bg-dark text-white border-secondary" : ""}
        >
          <Modal.Title className="fw-bold">Write Email</Modal.Title>
        </Modal.Header>

        <Modal.Body className={isDark ? "bg-dark text-white" : ""}>
          <Row className="g-2">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text
                  className={
                    isDark ? "bg-secondary text-white border-secondary" : ""
                  }
                >
                  Use Template
                </InputGroup.Text>
                <Form.Select
                  value={composeUseTemplateId}
                  onChange={(e) => setComposeUseTemplateId(e.target.value)}
                  className={`${isDark ? "bg-dark text-white border-secondary" : ""}`}
                >
                  <option value="">(No template) - Start blank</option>
                  {emailTemplates.map((t) => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.template_name ||
                        t.course ||
                        `Template ${t.template_id}`}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>

            <Col md={8}>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Control
                    placeholder="Course (required)"
                    value={composeCourse}
                    onChange={(e) => setComposeCourse(e.target.value)}
                    className={`${isDark ? "bg-dark text-white border-secondary" : ""}`}
                  />
                </Col>
                <Col md={6}>
                  <Form.Control
                    placeholder="New Template Name (required)"
                    value={composeTemplateName}
                    onChange={(e) => setComposeTemplateName(e.target.value)}
                    className={`${isDark ? "bg-dark text-white border-secondary" : ""}`}
                  />
                </Col>
              </Row>
            </Col>

            <Col md={12}>
              <InputGroup>
                <Form.Control
                  placeholder="Subject (required)"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  className={`${isDark ? "bg-dark text-white border-secondary" : ""}`}
                />

                <Button
                  variant={isDark ? "outline-light" : "outline-success"}
                  onClick={handleAiSuggestFromTemplate}
                  disabled={
                    !selectedEnquiry || !composeUseTemplateId || loadingAiTpl
                  }
                  title="Generate AI suggestion from selected template description"
                >
                  {loadingAiTpl ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Suggesting...
                    </>
                  ) : (
                    <>
                      <MdOutlineTipsAndUpdates className="me-2" />
                      AI Suggest (Template)
                    </>
                  )}
                </Button>
              </InputGroup>
            </Col>

            {(aiTplError || aiTplSuggest) && (
              <Col md={12}>
                <div
                  className={`p-3 rounded ${isDark ? "border border-secondary" : "bg-light"}`}
                >
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="fw-bold">AI Suggestion (Template)</div>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant={isDark ? "outline-light" : "outline-secondary"}
                        onClick={() => copyToClipboard(aiTplSuggest)}
                        disabled={!aiTplSuggest}
                      >
                        <MdContentCopy className="me-1" />
                        Copy
                      </Button>
                      <Button
                        size="sm"
                        variant={isDark ? "light" : "primary"}
                        onClick={applyAiSuggestionToBody}
                        disabled={!aiTplSuggest}
                      >
                        Use in Editor
                      </Button>
                    </div>
                  </div>

                  {aiTplError ? (
                    <div className="mt-2 text-danger small">{aiTplError}</div>
                  ) : (
                    <Form.Control
                      className={`mt-2 ${isDark ? "bg-dark text-white border-secondary" : ""}`}
                      as="textarea"
                      rows={6}
                      value={aiTplSuggest}
                      onChange={(e) => setAiTplSuggest(e.target.value)}
                      style={{ whiteSpace: "pre-wrap" }}
                    />
                  )}
                </div>
              </Col>
            )}

            <Col md={12}>
              <div className={`ck-wrap ${isDark ? "ck-wrap-dark" : ""}`}>
                <CKEditor
                  editor={ClassicEditor}
                  data={composeBody || ""}
                  onChange={(event, editor) => setComposeBody(editor.getData())}
                />
              </div>
            </Col>

            <Col md={12}>
              <Form.Group>
                <div className="d-flex align-items-center justify-content-between">
                  <Form.Label className={isDark ? "text-white" : ""}>
                    Attachments (optional)
                  </Form.Label>

                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant={isDark ? "outline-light" : "outline-primary"}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose Files
                    </Button>

                    {composeFiles.length > 0 && (
                      <Button
                        size="sm"
                        variant={isDark ? "outline-light" : "outline-secondary"}
                        onClick={() => {
                          setComposeFiles([]);
                          if (fileInputRef.current)
                            fileInputRef.current.value = "";
                        }}
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                </div>

                {/* ✅ hidden real input (removes "No file chosen") */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;

                    setComposeFiles((prev) => [...prev, ...files]);

                    // reset so same file can be selected again
                    e.target.value = "";
                  }}
                />

                {/* ✅ status text */}
                <div
                  className={`small mt-1 ${isDark ? "text-white-50" : "text-muted"}`}
                >
                  {composeFiles.length === 0
                    ? "No files selected"
                    : `${composeFiles.length} file(s) selected`}
                </div>

                {/* ✅ file list */}
                {composeFiles.length > 0 && (
                  <div
                    className={`mt-2 p-2 rounded ${
                      isDark ? "border border-secondary" : "bg-light"
                    }`}
                    style={{ maxHeight: 170, overflowY: "auto" }}
                  >
                    {composeFiles.map((f, idx) => (
                      <div
                        key={`${f.name}-${f.size}-${idx}`}
                        className="d-flex align-items-center justify-content-between gap-2 py-1"
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 13,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={f.name}
                          >
                            {f.name}
                          </div>
                          <div
                            className={isDark ? "text-white-50" : "text-muted"}
                            style={{ fontSize: 12 }}
                          >
                            {(f.size / 1024).toFixed(1)} KB
                          </div>
                        </div>

                        <Button
                          size="sm"
                          variant={isDark ? "outline-light" : "outline-danger"}
                          onClick={() => {
                            setComposeFiles((prev) =>
                              prev.filter((_, i) => i !== idx),
                            );
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className={isDark ? "bg-dark border-secondary" : ""}>
          <Button
            variant="secondary"
            onClick={() => {
              setShowWriteModal(false);
              resetCompose();
            }}
            disabled={savingAndSending || savingOnly}
          >
            Close
          </Button>

          <Button
            variant={isDark ? "outline-light" : "outline-primary"}
            onClick={handleSaveOnly}
            disabled={savingAndSending || savingOnly}
          >
            {savingOnly ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <MdEmail className="me-2" />
                Save Template Only
              </>
            )}
          </Button>

          <Button
            style={{ backgroundColor: "#1a3483", color: "#f3f3f5ff" }}
            onClick={handleSaveAndSend}
            disabled={savingAndSending || savingOnly}
          >
            {savingAndSending ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving & Sending...
              </>
            ) : (
              <>
                <FaPaperPlane className="me-2" />
                Save & Send
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .ck-wrap .ck-editor__editable { min-height: 320px; }
        .ck-wrap-dark .ck.ck-editor__main > .ck-editor__editable { background: #121212; color: #fff; }
        .ck-wrap-dark .ck.ck-toolbar { background: #1f1f1f; border-color: #444; }
        .ck-wrap-dark .ck.ck-toolbar .ck-button,
        .ck-wrap-dark .ck.ck-toolbar .ck-toolbar__separator { color: #fff; }
        .ck-wrap-dark .ck.ck-editor__editable_inline { border-color: #444; }

        /* sorting hover */
        .enq-thead th[role="button"]:hover {
          text-decoration: underline;
          opacity: 0.95;
        }

        /* Tag pill */
        .enq-tag-pill{
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.2px;
          line-height: 1;
          text-transform: lowercase;
          max-width: 220px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .enq-tag-dot{
          width: 8px;
          height: 8px;
          border-radius: 999px;
          flex: 0 0 auto;
          box-shadow: 0 0 0 2px rgba(255,255,255,0.35);
        }
      `}</style>
    </Container>
  );
};

export default Enquire;